import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    collection, query, onSnapshot, doc, addDoc, 
    updateDoc, deleteDoc, orderBy, runTransaction, serverTimestamp 
} from 'firebase/firestore';
import { db, auth, storage } from '../App';
import { printTicketReport, printFinishedTicketsReport } from '../utils/reports';
import DocumentosFiscais from '../components/DocumentosFiscais';
import { FATURAMENTO_PELO_FINANCEIRO } from '../config/featureFlags';
import { appId } from '../config/firebaseConfig';

// --- COMPONENTES AUXILIARES ---

const FormModal = ({ title, children, onClose, onSave }) => (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[150] p-4 font-sans">
        <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col border border-slate-700">
            <header className="p-6 border-b border-slate-700 flex justify-between items-center">
                <h3 className="font-bold text-2xl text-cyan-300 uppercase tracking-tight">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl">×</button>
            </header>
            <div className="p-6 overflow-y-auto flex-grow space-y-4 custom-scrollbar">{children}</div>
            <footer className="p-6 border-t border-slate-700 mt-auto flex gap-4">
                <button type="button" onClick={onClose} className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 rounded-md transition-colors uppercase text-xs tracking-widest">Cancelar</button>
                <button type="button" onClick={onSave} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-md transition-colors uppercase text-xs tracking-widest">Salvar</button>
            </footer>
        </div>
    </div>
);

// MODAL DE GERENCIAMENTO DE STATUS (AGORA COM EDIÇÃO)
const StatusManagerModal = ({ isOpen, onClose, statuses, onAdd, onUpdate, onDelete }) => {
    const [name, setName] = useState('');
    const [color, setColor] = useState('#60a5fa');
    const [editingId, setEditingId] = useState(null);

    const handleSubmit = () => {
        if (!name.trim()) return;
        if (editingId) {
            onUpdate(editingId, { name, color });
            setEditingId(null);
        } else {
            onAdd({ name, color });
        }
        setName('');
        setColor('#60a5fa');
    };

    const handleEdit = (status) => {
        setEditingId(status.id);
        setName(status.name);
        setColor(status.color);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[120] p-4 font-sans">
            <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh] border border-slate-700">
                <header className="p-6 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="font-bold text-xl text-cyan-300 uppercase tracking-widest">Gerenciar Status</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
                </header>
                <div className="p-6 overflow-y-auto space-y-4 custom-scrollbar">
                    <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 space-y-3">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                            {editingId ? 'Editando Status' : 'Novo Status'}
                        </label>
                        <div className="flex gap-2">
                            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-12 h-10 rounded border-none bg-transparent cursor-pointer" title="Escolher cor" />
                            <input type="text" placeholder="Nome do Status" value={name} onChange={e => setName(e.target.value)} className="flex-1 bg-slate-700 border border-slate-600 rounded p-2 text-white outline-none focus:ring-1 focus:ring-cyan-500" />
                            <button onClick={handleSubmit} className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 rounded font-bold transition-colors">
                                {editingId ? 'Salvar' : 'Add'}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-2">
                        {statuses.map(s => (
                            <div key={s.id} className="bg-slate-700/80 p-3 rounded-lg border border-slate-600/40 flex justify-between items-center group hover:border-slate-500 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 rounded-full border border-white/10 shrink-0" style={{backgroundColor: s.color}}></div>
                                    <span className="text-white font-medium text-sm">{s.name}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button 
                                        type="button"
                                        onClick={() => handleEdit(s)} 
                                        className="text-slate-400 hover:text-cyan-400 p-1.5 rounded hover:bg-slate-600 transition-colors"
                                        title="Editar"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => onDelete(s.id)} 
                                        className="text-slate-400 hover:text-red-400 p-1.5 rounded hover:bg-slate-600 transition-colors"
                                        title="Excluir"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <footer className="p-4 border-t border-slate-700 bg-slate-800 flex justify-end">
                    <button onClick={onClose} className="bg-slate-700 hover:bg-slate-600 text-white py-2 px-5 rounded-lg font-bold uppercase text-xs tracking-wider transition-colors">Fechar</button>
                </footer>
            </div>
        </div>
    );
};

const LocalConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[150] p-4 font-sans">
            <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-md border border-slate-700 p-8 text-center animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
                <p className="text-slate-300 mb-8 leading-relaxed">{message}</p>
                <div className="flex gap-4">
                    <button onClick={onCancel} className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 rounded-lg">Cancelar</button>
                    <button onClick={onConfirm} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-lg shadow-lg">Confirmar</button>
                </div>
            </div>
        </div>
    );
};

const AssignedToMultiSelect = ({ users, assignedUsers, onChange }) => {
    return (
        <div className="space-y-2 max-h-60 overflow-y-auto p-2 bg-slate-900/50 rounded-lg border border-slate-700 custom-scrollbar font-sans">
            {users.map(user => (
                <label key={user.uid} className="flex items-center gap-3 p-3 hover:bg-slate-800/80 cursor-pointer rounded-lg transition-colors font-sans">
                    <input 
                        type="checkbox" 
                        checked={assignedUsers.some(u => u.uid === user.uid)} 
                        onChange={() => {
                            const isAssigned = assignedUsers.some(u => u.uid === user.uid);
                            onChange(isAssigned ? assignedUsers.filter(u => u.uid !== user.uid) : [...assignedUsers, {uid: user.uid, email: user.email}]);
                        }} 
                        className="form-checkbox text-cyan-500 rounded h-5 w-5 border-slate-600 bg-slate-700" 
                    />
                    <span className="text-white text-sm font-medium">{user.email}</span>
                </label>
            ))}
        </div>
    );
};

// --- PÁGINA PRINCIPAL ---

export default function TicketsPage({ permissionLevel, navigateTo, allowedCompanies, uiItems = {}, navTrigger }) {
    const isUserAdmin = useMemo(() => auth.currentUser?.email === 'jonathan@arcusinformatica.com.br', [auth.currentUser]);

    // Função helper para verificar permissão granular
    const isAllowed = (itemId) => {
        if (isUserAdmin) return true;
        return uiItems[itemId] !== false; // Default true se não estiver explicitamente bloqueado
    };

    const [rawTickets, setRawTickets] = useState([]);
    const [users, setUsers] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [ticketClients, setTicketClients] = useState([]);
    const [launchTemplates, setLaunchTemplates] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    const [clientsContrato, setClientsContrato] = useState([]);
    const [clientsAvulsos, setClientsAvulsos] = useState([]);

    const allCompanyList = useMemo(() => {
        return [...clientsContrato, ...clientsAvulsos];
    }, [clientsContrato, clientsAvulsos]);
    
    const companyList = useMemo(() => {
        return allCompanyList.filter(c => c.ativo !== false && c.visibilidadeModulos?.tickets !== false);
    }, [allCompanyList]);

    const dropdownCompanies = useMemo(() => {
        let list = [];
        if (allowedCompanies === null) {
            list = companyList.map(c => ({
                id: c.id,
                name: c.name,
                displayName: c.nomeFantasia || c.name
            }));
        } else {
            list = (allowedCompanies || []).map(compName => {
                const found = companyList.find(c => c.name === compName || c.nomeFantasia === compName);
                return {
                    id: found ? found.id : compName,
                    name: found ? found.name : compName,
                    displayName: found ? (found.nomeFantasia || found.name) : compName
                };
            });
        }
        return list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [allowedCompanies, companyList]);

    const getCompanyDisplayName = (companyNameOrId) => {
        if (!companyNameOrId) return '';
        const found = allCompanyList.find(c => c.id === companyNameOrId || c.name === companyNameOrId || c.nomeFantasia === companyNameOrId);
        return found ? (found.nomeFantasia || found.name) : companyNameOrId;
    };

    // Filtro de ingressos por empresa permitida do técnico (com cruzamento inteligente de Razão Social e Nome Fantasia)
    const tickets = useMemo(() => {
        if (allowedCompanies === null) return rawTickets;
        return rawTickets.filter(t => {
            // 1. Match direto por ID ou Nome gravado no chamado
            if (allowedCompanies.includes(t.clientCompanyId) || 
                allowedCompanies.includes(t.clientCompany) || 
                (t.clientCompanyName && allowedCompanies.includes(t.clientCompanyName))) {
                return true;
            }
            // 2. Match cruzando com o cadastro da empresa (ID, Razão Social ou Nome Fantasia)
            const comp = allCompanyList.find(c => 
                c.id === t.clientCompanyId || 
                (c.name && t.clientCompany && c.name.toLowerCase().trim() === t.clientCompany.toLowerCase().trim()) ||
                (c.nomeFantasia && t.clientCompany && c.nomeFantasia.toLowerCase().trim() === t.clientCompany.toLowerCase().trim())
            );
            if (comp) {
                return (
                    allowedCompanies.includes(comp.id) ||
                    allowedCompanies.includes(comp.name) ||
                    (comp.nomeFantasia && allowedCompanies.includes(comp.nomeFantasia))
                );
            }
            return false;
        });
    }, [rawTickets, allowedCompanies, allCompanyList]);

    const [viewMode, setViewMode] = useState('list'); 
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('Novo Chamado');
    const [filterScheduled, setFilterScheduled] = useState('all');
    const [filterUser, setFilterUser] = useState('all');

    const [selectedTicket, setSelectedTicket] = useState(null);

    // Reset to list view when navigation is triggered
    useEffect(() => {
        if (navTrigger > 0) {
            setViewMode('list');
            setSelectedTicket(null);
        }
    }, [navTrigger]);

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadingDirect, setUploadingDirect] = useState(false);
    const [selectedImageModal, setSelectedImageModal] = useState(null);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('add');
    const [clientSearch, setClientSearch] = useState('');
    const [showClientResults, setShowClientResults] = useState(false);
    
    // CONFIGURAÇÕES (ENGRENAGEM)
    const [isManageStatusDropdownOpen, setIsManageStatusDropdownOpen] = useState(false);
    const [isStatusManagerOpen, setIsStatusManagerOpen] = useState(false);
    
    const [activeModal, setActiveModal] = useState(null); 
    const [tempLaunchData, setTempLaunchData] = useState({ templateId: '', templateName: '', fieldName: '', value: '' });
    const [tempStatus, setTempStatus] = useState('');
    const [templateToRemove, setTemplateToRemove] = useState(null);
    const [ticketToReopen, setTicketToReopen] = useState(null);

    const [newTicket, setNewTicket] = useState({ title: '', description: '', priority: 'low', assignedTo: [], assignedToEmail: [], status: '', clientName: '', clientEmail: '', clientWhatsApp: '', clientCompany: '' });

    const [successMessage, setSuccessMessage] = useState('');
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [tempScheduleDate, setTempScheduleDate] = useState('');
    const [tempScheduleTime, setTempScheduleTime] = useState('');
    const [tempScheduleType, setTempScheduleType] = useState('Remoto');

    // Finished tickets modal states
    const [isCloseTicketModalOpen, setIsCloseTicketModalOpen] = useState(false);
    const [isInvoiceChoiceModalOpen, setIsInvoiceChoiceModalOpen] = useState(false);
    const [closeCompanyConfirmed, setCloseCompanyConfirmed] = useState(false);
    const [closeSelectedCompanyId, setCloseSelectedCompanyId] = useState('');
    const [isFinishedModalOpen, setIsFinishedModalOpen] = useState(false);
    const [finishedSearch, setFinishedSearch] = useState('');
    const [finishedClient, setFinishedClient] = useState('all');
    const [finishedFilterMonth, setFinishedFilterMonth] = useState(new Date().getMonth());
    const [finishedFilterYear, setFinishedFilterYear] = useState(new Date().getFullYear());
    const [finishedShowAllPeriod, setFinishedShowAllPeriod] = useState(false);
    // Intervalo de datas (De / Até) nos Chamados Finalizados
    const [finishedUseRange, setFinishedUseRange] = useState(false);
    const [finishedDateFrom, setFinishedDateFrom] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    });
    const [finishedDateTo, setFinishedDateTo] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
    const finishedPeriodMode = finishedUseRange ? 'range' : (finishedShowAllPeriod ? 'all' : 'month');
    const setFinishedPeriodMode = (mode) => {
        setFinishedUseRange(mode === 'range');
        setFinishedShowAllPeriod(mode === 'all');
    };
    const [finishedClientType, setFinishedClientType] = useState('all');
    const [finishedStatusFilter, setFinishedStatusFilter] = useState('all');
    const [isLinkCompanyModalOpen, setIsLinkCompanyModalOpen] = useState(false);
    const [linkCompanySelectedId, setLinkCompanySelectedId] = useState('');
    const [linkProtocolo, setLinkProtocolo] = useState('');
    const [linkAutoAssign, setLinkAutoAssign] = useState(true);
    const [isDashboardOpen, setIsDashboardOpen] = useState(false);
    const [showAllCompanies, setShowAllCompanies] = useState(false);
    const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
    const [dashDrill, setDashDrill] = useState(null); // { title, tickets }
    const [companyStatsSearch, setCompanyStatsSearch] = useState('');
    const [cameFromFinishedModal, setCameFromFinishedModal] = useState(false);
    const [returnToFinanceiroId, setReturnToFinanceiroId] = useState(null); // chamado aberto a partir do Financeiro
    const pendingOpenTicketRef = useRef((() => {
        try {
            const id = sessionStorage.getItem('arcus_open_ticket_id');
            if (id) sessionStorage.removeItem('arcus_open_ticket_id');
            return id;
        } catch (e) { return null; }
    })());
    const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
    const [billingDueDate, setBillingDueDate] = useState('');
    const [billingLoading, setBillingLoading] = useState(false);

    const handlePrevMonth = () => {
        setFinishedShowAllPeriod(false);
        setFinishedUseRange(false);
        setFinishedFilterMonth(prev => {
            if (prev === 0) {
                setFinishedFilterYear(y => y - 1);
                return 11;
            }
            return prev - 1;
        });
    };

    const handleNextMonth = () => {
        setFinishedShowAllPeriod(false);
        setFinishedUseRange(false);
        setFinishedFilterMonth(prev => {
            if (prev === 11) {
                setFinishedFilterYear(y => y + 1);
                return 0;
            }
            return prev + 1;
        });
    };

    const [servicesPriceTable, setServicesPriceTable] = useState([]);
    const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [serviceQuantity, setServiceQuantity] = useState(1);
    const [serviceSearchText, setServiceSearchText] = useState('');
    const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
    const [isCustomService, setIsCustomService] = useState(false);
    const [customServiceName, setCustomServiceName] = useState('');
    const [customServiceValue, setCustomServiceValue] = useState('');

    const isFinished = (statusName) => {
        const s = statusName?.toLowerCase() || '';
        return s.includes('finalizado') || s.includes('concluido') || s.includes('concluído') || s.includes('fechado') || s.includes('faturado');
    };

    const manageDropdownRef = useRef(null);
    const paths = useMemo(() => ({
        tickets: `/artifacts/${appId}/public/data/tickets`,
        users: `/artifacts/${appId}/public/data/user_permissions`,
        statuses: `/artifacts/${appId}/public/data/ticket_statuses`,
        clients: `/artifacts/${appId}/public/data/clientes_chamados`, 
        launchInfo: `/artifacts/${appId}/public/data/launch_infos`, 
        counter: `/artifacts/${appId}/public/data/counters/ticket_counter`,
        groups: `/artifacts/${appId}/public/data/groups`
    }), []);

    const filteredUsersForCompany = useMemo(() => {
        if (!newTicket.clientCompany) return [];
        // Encontrar o grupo que tem essa empresa
        const targetGroup = groups.find(g => g.allowedCompanies?.includes(newTicket.clientCompany));
        if (!targetGroup) return [];
        // Retornar usuários desse grupo
        return users.filter(u => u.groupId === targetGroup.id);
    }, [newTicket.clientCompany, groups, users]);

    const selectedTicketRef = useRef(selectedTicket);
    useEffect(() => {
        selectedTicketRef.current = selectedTicket;
    }, [selectedTicket]);

    useEffect(() => {
        const unsubGroups = onSnapshot(collection(db, paths.groups), (snap) => {
            setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubTickets = onSnapshot(query(collection(db, paths.tickets), orderBy('createdAt', 'desc')), snap => {
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRawTickets(data);
            setLoading(false);
            if (selectedTicketRef.current) {
                const updated = data.find(t => t.id === selectedTicketRef.current.id);
                if (updated) {
                    setSelectedTicket(updated);
                }
            }
        });
        const unsubUsers = onSnapshot(collection(db, paths.users), snap => setUsers(snap.docs.map(doc => ({ uid: doc.id, ...doc.data() }))));
        const unsubStatuses = onSnapshot(query(collection(db, paths.statuses), orderBy('name', 'asc')), (snap) => {
            const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setStatuses(list);
        });
        
        const unsubClients = onSnapshot(collection(db, paths.clients), snap => {
            let data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            if (allowedCompanies !== null) {
                data = data.filter(c => allowedCompanies.includes(c.company) || allowedCompanies.includes(c.name));
            }
            setTicketClients(data);
        });

        const unsubClientsContrato = onSnapshot(collection(db, `/artifacts/${appId}/public/data/clients`), snap => {
            setClientsContrato(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubClientsAvulsos = onSnapshot(collection(db, `/artifacts/${appId}/public/data/clients-avulsos`), snap => {
            setClientsAvulsos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubLaunch = onSnapshot(query(collection(db, paths.launchInfo), orderBy('name', 'asc')), snap => setLaunchTemplates(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        const unsubServices = onSnapshot(collection(db, `/artifacts/${appId}/public/data/service_values`), snap => {
            setServicesPriceTable(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => { unsubTickets(); unsubUsers(); unsubStatuses(); unsubClients(); unsubLaunch(); unsubClientsContrato(); unsubClientsAvulsos(); unsubServices(); };
    }, [paths, allowedCompanies]);

    useEffect(() => {
        if (!selectedTicket || viewMode !== 'detail') { setComments([]); return; }
        const q = query(collection(db, `${paths.tickets}/${selectedTicket.id}/comments`), orderBy('createdAt', 'asc'));
        return onSnapshot(q, snap => setComments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    }, [selectedTicket?.id, viewMode, paths.tickets]);

    useEffect(() => {
        const handleClickOutside = (e) => { if (manageDropdownRef.current && !manageDropdownRef.current.contains(e.target)) setIsManageStatusDropdownOpen(false); };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredTickets = useMemo(() => {
        let filtered = tickets;
        if (filterStatus === 'all') {
            filtered = filtered.filter(t => !isFinished(t.status));
        } else {
            filtered = filtered.filter(t => t.status === filterStatus);
        }
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(t => 
                t.title.toLowerCase().includes(lowerSearch) || 
                String(t.numeroSequencial).includes(lowerSearch) || 
                (`a${t.numeroSequencial}`).includes(lowerSearch) ||
                (t.clientName && t.clientName.toLowerCase().includes(lowerSearch)) ||
                (t.clientCompany && t.clientCompany.toLowerCase().includes(lowerSearch)) ||
                (getCompanyDisplayName(t.clientCompanyId || t.clientCompany).toLowerCase().includes(lowerSearch))
            );
        }
        if (filterScheduled !== 'all') {
            if (filterScheduled === 'scheduled') {
                filtered = filtered.filter(t => t.isScheduled);
            } else if (filterScheduled === 'not_scheduled') {
                filtered = filtered.filter(t => !t.isScheduled);
            }
        }
        if (filterUser !== 'all') filtered = filtered.filter(t => t.assignedTo?.includes(filterUser));

        if (filterStatus === 'all') {
            filtered = [...filtered].sort((a, b) => {
                const s1 = a.status || '';
                const s2 = b.status || '';
                const cmp = s1.localeCompare(s2);
                if (cmp !== 0) return cmp;
                return (b.numeroSequencial || 0) - (a.numeroSequencial || 0);
            });
        }

        return filtered;
    }, [tickets, searchTerm, filterStatus, filterScheduled, filterUser]);

    const finishedTickets = useMemo(() => {
        let filtered = tickets.filter(t => isFinished(t.status));
        
        if (finishedSearch) {
            const lowerSearch = finishedSearch.toLowerCase();
            filtered = filtered.filter(t => 
                t.title.toLowerCase().includes(lowerSearch) || 
                String(t.numeroSequencial).includes(lowerSearch) || 
                (`a${t.numeroSequencial}`).includes(lowerSearch) ||
                (t.clientName && t.clientName.toLowerCase().includes(lowerSearch)) ||
                (t.description && t.description.toLowerCase().includes(lowerSearch)) ||
                (t.clientCompany && t.clientCompany.toLowerCase().includes(lowerSearch)) ||
                (getCompanyDisplayName(t.clientCompanyId || t.clientCompany).toLowerCase().includes(lowerSearch))
            );
        }
        
        if (finishedClient !== 'all') {
            const selectedComp = typeof allCompanyList !== 'undefined' ? allCompanyList.find(c => c.name === finishedClient || c.id === finishedClient) : null;
            if (selectedComp) {
                filtered = filtered.filter(t => 
                    t.clientCompanyId === selectedComp.id || 
                    t.clientCompany === selectedComp.name ||
                    t.clientCompany === selectedComp.id
                );
            } else {
                filtered = filtered.filter(t => 
                    t.clientCompanyId === finishedClient || 
                    t.clientCompany === finishedClient
                );
            }
        }
        
        if (finishedClientType !== 'all') {
            filtered = filtered.filter(t => {
                const isContrato = clientsContrato.some(c => c.id === t.clientCompanyId || c.name === t.clientCompany) || t.status?.includes('Contrato') || t.status?.includes('Sem Cobrança');
                const isAvulso = clientsAvulsos.some(c => c.id === t.clientCompanyId || c.name === t.clientCompany) || t.status?.includes('Avulso') || t.status?.includes('Com Cobrança');
                if (finishedClientType === 'contrato') return isContrato;
                if (finishedClientType === 'avulso') return isAvulso;
                return true;
            });
        }
        
        if (finishedUseRange) {
            filtered = filtered.filter(t => {
                if (!t.createdAt) return false;
                const date = t.createdAt.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
                const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                if (finishedDateFrom && ymd < finishedDateFrom) return false;
                if (finishedDateTo && ymd > finishedDateTo) return false;
                return true;
            });
        } else if (!finishedShowAllPeriod) {
            filtered = filtered.filter(t => {
                if (!t.createdAt) return false;
                const date = t.createdAt.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
                return date.getMonth() === finishedFilterMonth && date.getFullYear() === finishedFilterYear;
            });
        }
        
        if (finishedStatusFilter !== 'all') {
            if (finishedStatusFilter === 'faturados') {
                filtered = filtered.filter(t => t.status === 'Faturado');
            } else if (finishedStatusFilter === 'com_valor') {
                filtered = filtered.filter(t => t.totalAmount > 0);
            } else {
                filtered = filtered.filter(t => t.status === finishedStatusFilter);
            }
        }
        
        return filtered;
    }, [tickets, finishedSearch, finishedClient, finishedFilterMonth, finishedFilterYear, finishedShowAllPeriod, finishedUseRange, finishedDateFrom, finishedDateTo, finishedClientType, finishedStatusFilter, clientsContrato, clientsAvulsos]);

    const dashboardStats = useMemo(() => {
        const total = tickets.length;
        const active = tickets.filter(t => !isFinished(t.status)).length;
        const finished = tickets.filter(t => isFinished(t.status)).length;
        const scheduled = tickets.filter(t => t.isScheduled).length;

        // Faturamento
        let totalRevenue = 0;
        let contratoRevenue = 0;
        let avulsoRevenue = 0;
        let openRevenue = 0;
        const revenueLists = { all: [], contrato: [], avulso: [], open: [] };

        // --- Resolução robusta de empresa (ID, Razão Social ou Nome Fantasia; sem diferenciar maiúsculas/acentos/espaços) ---
        const normalize = (v) => String(v || '')
            .toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        const contratoIds = new Set(clientsContrato.map(c => c.id));
        const companyIndex = new Map();
        allCompanyList.forEach(c => {
            if (c.id && !companyIndex.has(c.id)) companyIndex.set(c.id, c);
            [c.name, c.nomeFantasia].forEach(n => {
                const k = normalize(n);
                if (k && !companyIndex.has(k)) companyIndex.set(k, c);
            });
        });
        const resolveCompany = (t) => {
            const candidates = [t.clientCompanyId, t.clientCompany, t.clientCompanyName];
            for (const cand of candidates) {
                if (!cand) continue;
                const found = companyIndex.get(cand) || companyIndex.get(normalize(cand));
                if (found) return found;
            }
            return null;
        };
        // Placeholders que significam "empresa ainda não vinculada"
        const UNLINKED = new Set(['', 'smbot - whatsapp', 'sem empresa', 'nao cadastrado', 'nao cadastrada']);

        tickets.forEach(t => {
            const amt = Number(t.totalAmount) || 0;
            if (!amt) return;
            totalRevenue += amt;
            revenueLists.all.push(t);
            const comp = resolveCompany(t);
            const tipo = t.faturamentoTipo;
            const st = t.status || '';
            if (tipo === 'sem_cobranca' || st.includes('Contrato') || st.includes('Sem Cobrança')) {
                contratoRevenue += amt; revenueLists.contrato.push(t);
            } else if (tipo === 'com_nota' || tipo === 'sem_nota' || st === 'Faturado' || st.includes('Avulso') || st.includes('Com Cobrança')) {
                avulsoRevenue += amt; revenueLists.avulso.push(t);
            } else if (!isFinished(st)) {
                openRevenue += amt; revenueLists.open.push(t);
            } else if (comp && contratoIds.has(comp.id)) {
                contratoRevenue += amt; revenueLists.contrato.push(t);
            } else {
                avulsoRevenue += amt; revenueLists.avulso.push(t);
            }
        });

        // Chamados por status
        const statusCounts = {};
        const statusTickets = {};
        tickets.forEach(t => {
            statusCounts[t.status] = (statusCounts[t.status] || 0) + 1;
            (statusTickets[t.status] = statusTickets[t.status] || []).push(t);
        });

        // Chamados por Técnico (email)
        const techCounts = {};
        const techTickets = {};
        tickets.forEach(t => {
            if (t.assignedToEmail && t.assignedToEmail.length > 0) {
                t.assignedToEmail.forEach(email => {
                    const name = email.split('@')[0];
                    techCounts[name] = (techCounts[name] || 0) + 1;
                    (techTickets[name] = techTickets[name] || []).push(t);
                });
            } else {
                techCounts['Sem atribuição'] = (techCounts['Sem atribuição'] || 0) + 1;
                (techTickets['Sem atribuição'] = techTickets['Sem atribuição'] || []).push(t);
            }
        });

        // Chamados por Empresa (agrupado pelo cadastro real; variações de escrita são unificadas)
        const companyMap = new Map(); // chave normalizada -> { name, count }
        let unlinkedCount = 0;
        const unlinkedTickets = [];
        tickets.forEach(t => {
            const comp = resolveCompany(t);
            let label;
            if (comp) {
                label = comp.nomeFantasia || comp.name || comp.id;
            } else {
                const raw = t.clientCompanyName || t.clientCompany || t.clientCompanyId || '';
                if (UNLINKED.has(normalize(raw))) { unlinkedCount++; unlinkedTickets.push(t); return; }
                label = raw;
            }
            const key = normalize(label);
            const entry = companyMap.get(key) || { name: label, count: 0, registered: !!comp, tickets: [] };
            entry.count += 1;
            entry.tickets.push(t);
            if (comp) entry.registered = true;
            companyMap.set(key, entry);
        });
        const companyRanking = [...companyMap.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
        const maxSeq = tickets.reduce((m, t) => Math.max(m, Number(t.numeroSequencial) || 0), 0);

        return {
            total, active, finished, scheduled,
            totalRevenue, contratoRevenue, avulsoRevenue, openRevenue,
            statusCounts, techCounts, companyRanking, unlinkedCount, maxSeq,
            revenueLists, statusTickets, techTickets, unlinkedTickets
        };
    }, [tickets, statuses, allCompanyList, clientsContrato, clientsAvulsos]);

    const handlePrintFinishedReport = () => {
        const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
        const ymdToBR = (v) => (v ? v.split('-').reverse().join('/') : '...');
        const periodText = finishedUseRange
            ? `${ymdToBR(finishedDateFrom)} a ${ymdToBR(finishedDateTo)}`
            : (finishedShowAllPeriod ? 'Todo o período' : `${MONTHS[finishedFilterMonth]}/${finishedFilterYear}`);

        const statusLabel = { all: 'Todos', faturados: 'Apenas Faturados', com_valor: 'Com valor' }[finishedStatusFilter] || finishedStatusFilter;
        const typeLabel = { all: 'Todos', contrato: 'Contrato', avulso: 'Avulso' }[finishedClientType] || finishedClientType;
        const parts = [`Status: ${statusLabel}`, `Clientes: ${typeLabel}`, `Empresa: ${finishedClient === 'all' ? 'Todas' : getCompanyDisplayName(finishedClient)}`];
        if (finishedSearch) parts.push(`Busca: "${finishedSearch}"`);

        const ok = printFinishedTicketsReport(finishedTickets, {
            getCompanyName: (t) => getCompanyDisplayName(t.clientCompanyId || t.clientCompany),
            getStatusColor: (st) => statuses.find(s => s.name === st)?.color
                || (st === 'Faturado' ? '#10b981' : (st === 'Finalizado Contrato' ? '#3b82f6' : '#06b6d4')),
            periodText,
            filtersText: parts.join(' · '),
            emittedBy: auth.currentUser?.email || ''
        });
        if (!ok) alert('O navegador bloqueou a janela de impressão. Libere pop-ups para este site.');
    };

    const handlePrintTicket = (includeInternal) => {
        setIsPrintMenuOpen(false);
        if (!selectedTicket) return;
        const ok = printTicketReport(selectedTicket, {
            comments,
            includeInternal,
            companyName: getCompanyDisplayName(selectedTicket.clientCompanyId || selectedTicket.clientCompany) || selectedTicket.clientCompany || '',
            statusColor: statuses.find(s => s.name === selectedTicket.status)?.color || '#0891b2',
            templates: launchTemplates.filter(t => (selectedTicket.addedTemplates || []).includes(t.id)),
            users,
            emittedBy: auth.currentUser?.email || ''
        });
        if (!ok) alert('O navegador bloqueou a janela de impressão. Libere pop-ups para este site.');
    };

    // Atalho vindo do Financeiro: abre o chamado assim que a lista carregar
    useEffect(() => {
        const pendingId = pendingOpenTicketRef.current;
        if (!pendingId || loading) return;
        pendingOpenTicketRef.current = null;
        const target = tickets.find(t => t.id === pendingId);
        if (target) {
            setReturnToFinanceiroId(pendingId);
            handleOpenTicketDetail(target);
        } else {
            console.warn('Financeiro → Chamados: chamado não encontrado ou sem acesso:', pendingId);
        }
    }, [loading, tickets]);

    const handleOpenTicketDetail = (ticket) => {
        setSelectedTicket(ticket);
        setViewMode('detail');
        
        const companyName = getCompanyDisplayName(ticket.clientCompanyId || ticket.clientCompany) || '';
        const isUnregistered = companyName.toLowerCase().includes('smbot');
        const hasProtocolo = !!ticket.protocolo;
        
        if (!isUnregistered && hasProtocolo) {
            setLinkCompanySelectedId(ticket.clientCompanyId || ticket.clientCompany || '');
            setLinkProtocolo(ticket.protocolo);
        } else {
            setIsLinkCompanyModalOpen(true);
            setLinkCompanySelectedId(isUnregistered ? '' : (ticket.clientCompanyId || ticket.clientCompany || ''));
            setLinkProtocolo(ticket.protocolo || '');
        }
    };

    const handleInvoiceTicket = async () => {
        if (!selectedTicket || !billingDueDate) return;
        
        const companyObj = allCompanyList.find(c => c.id === selectedTicket.clientCompanyId || c.name === selectedTicket.clientCompany || c.nomeFantasia === selectedTicket.clientCompany);
        const idClienteVhsys = companyObj?.vhsysId;
        
        if (!idClienteVhsys) {
            alert(`Erro: A empresa "${companyObj?.nomeFantasia || selectedTicket.clientCompany}" não possui um ID do VHSYS associado. Configure o ID do VHSYS no cadastro do cliente.`);
            return;
        }
        
        setBillingLoading(true);
        try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const lancarFaturamentoFn = httpsCallable(functions, 'lancarFaturamentoNoVhsys');
            
            const res = await lancarFaturamentoFn({
                idClienteVhsys: idClienteVhsys,
                valor: selectedTicket.totalAmount,
                descricao: `Chamado #${selectedTicket.numeroSequencial} - ${selectedTicket.title}`,
                dataVencimento: billingDueDate
            });
            
            if (res.data?.success) {
                await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                    status: 'Faturado',
                    vhsysReceitaId: String(res.data.id_receita || '')
                });
                
                await handleSendComment(true, `**Chamado Faturado no VHSYS**\nReceita ID: ${res.data.id_receita}\nVencimento: ${billingDueDate.split('-').reverse().join('/')}`);
                
                setSelectedTicket(prev => ({
                    ...prev,
                    status: 'Faturado',
                    vhsysReceitaId: String(res.data.id_receita || '')
                }));
                
                setIsBillingModalOpen(false);
                setSuccessMessage("Chamado faturado com sucesso no VHSYS!");
            } else {
                alert("Erro ao faturar chamado: " + (res.data?.message || "Erro desconhecido"));
            }
        } catch (err) {
            console.error("Erro na integração de faturamento:", err);
            alert("Erro ao faturar chamado: " + err.message);
        } finally {
            setBillingLoading(false);
        }
    };

    const handleCloseTicket = async (type, comNota = true) => {
        if (!selectedTicket) return;
        if (type !== 'contrato' && (!selectedTicket.services || selectedTicket.services.length === 0)) {
            alert("Não é possível fechar com cobrança sem antes lançar serviços/valores da Tabela de Preços.");
            return;
        }
        if (!closeCompanyConfirmed) {
            alert("Você precisa confirmar que o chamado está vinculado à empresa correta antes de fechar.");
            return;
        }

        try {
            setBillingLoading(true);

            // Se o técnico alterou a empresa durante o fechamento
            const currentCompId = selectedTicket.clientCompanyId || selectedTicket.clientCompany;
            let finalCompName = selectedTicket.clientCompany || '';
            let finalCompId = selectedTicket.clientCompanyId || '';
            let finalCompDisplayName = selectedTicket.clientCompanyName || finalCompName || '';

            if (closeSelectedCompanyId && closeSelectedCompanyId !== currentCompId) {
                const selectedComp = dropdownCompanies.find(c => c.id === closeSelectedCompanyId || c.name === closeSelectedCompanyId);
                finalCompName = selectedComp ? selectedComp.name : closeSelectedCompanyId;
                finalCompId = selectedComp ? selectedComp.id : closeSelectedCompanyId;
                finalCompDisplayName = selectedComp ? selectedComp.displayName : finalCompName;
            }

            let finalStatus = type === 'contrato' ? 'Finalizado (Sem Cobrança)' : 'Finalizado (Com Cobrança)';
            let vhsysNfseId = '';
            let vhsysRpsNumero = '';
            let vhsysReceitaId = '';

            // Se for Avulso, verifica se é Com Nota (NFS-e) ou Sem Nota (Contas a Receber)
            if (type === 'avulso') {
                const company = allCompanyList.find(c => c.id === finalCompId || c.name === finalCompName || c.nomeFantasia === finalCompName);
                if (!company || !company.vhsysId) {
                    alert("A empresa vinculada não possui um ID do VHSYS (vhsysId) configurado. Configure no cadastro de clientes antes de finalizar avulso.");
                    setBillingLoading(false);
                    return;
                }

                const idClienteVhsys = company.vhsysId;
                const companyNomeReal = company.name || company.nomeFantasia || finalCompName || 'Cliente';

                const { getFunctions, httpsCallable } = await import('firebase/functions');
                const functions = getFunctions();

                if (FATURAMENTO_PELO_FINANCEIRO) {
                    // Novo fluxo: o Financeiro fatura (individual ou agrupado). Nada é lançado no VHSYS agora.
                    finalStatus = 'Finalizado (Com Cobrança)';
                } else if (comNota) {
                    // CAMINHO 1: COM NOTA FISCAL (Cria Rascunho de NFS-e no VHSYS)
                    const lancarNotaFn = httpsCallable(functions, 'lancarNotaServicoNoVhsys');
                    
                    let descServicos = `Chamado A${selectedTicket.numeroSequencial} - ${selectedTicket.title}`;
                    if (selectedTicket.services && selectedTicket.services.length > 0) {
                        const itensTexto = selectedTicket.services.map(s => {
                            const precoItem = s.total !== undefined ? s.total : (s.value !== undefined ? s.value : (s.price || s.valor || 0));
                            return `${s.name || s.descricao} (R$ ${parseFloat(precoItem).toFixed(2)})`;
                        }).join(', ');
                        descServicos += `\nItens: ${itensTexto}`;
                    }

                    const res = await lancarNotaFn({
                        idClienteVhsys: idClienteVhsys,
                        nomeCliente: companyNomeReal,
                        valor: selectedTicket.totalAmount,
                        descricaoServicos: descServicos
                    });

                    if (res.data?.success) {
                        finalStatus = 'Faturado';
                        vhsysNfseId = String(res.data.id_servico || '');
                        vhsysRpsNumero = String(res.data.id_pedido || '');
                    } else {
                        alert("Erro ao criar rascunho de NFS-e no VHSYS: " + (res.data?.message || "Erro desconhecido"));
                        setBillingLoading(false);
                        return;
                    }
                } else {
                    // CAMINHO 2: SEM NOTA FISCAL (Lança direto em Contas a Receber no VHSYS)
                    const lancarFaturamentoFn = httpsCallable(functions, 'lancarFaturamentoNoVhsys');
                    const autoDueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

                    const rawDescription = `A${selectedTicket.numeroSequencial} ${selectedTicket.title}`;
                    const safeDescription = rawDescription.length > 40 ? rawDescription.substring(0, 40) : rawDescription;

                    const res = await lancarFaturamentoFn({
                        idClienteVhsys: idClienteVhsys,
                        valor: selectedTicket.totalAmount,
                        descricao: safeDescription,
                        dataVencimento: autoDueDate
                    });

                    if (res.data?.success) {
                        finalStatus = 'Faturado';
                        vhsysReceitaId = String(res.data.id_receita || '');
                    } else {
                        alert("Erro ao lançar contas a receber no VHSYS: " + (res.data?.message || "Erro desconhecido"));
                        setBillingLoading(false);
                        return;
                    }
                }
            }

            const ticketUpdateData = {
                status: finalStatus,
                closeNotes: selectedTicket.closeNotes || '',
                clientCompany: finalCompName,
                clientCompanyId: finalCompId,
                clientCompanyName: finalCompDisplayName,
                faturamentoTipo: type === 'avulso'
                    ? (FATURAMENTO_PELO_FINANCEIRO ? 'a_faturar' : (comNota ? 'com_nota' : 'sem_nota'))
                    : 'sem_cobranca'
            };
            if (type === 'avulso' && FATURAMENTO_PELO_FINANCEIRO) {
                // Indicação do técnico para o Financeiro (não lança nada no VHSYS)
                ticketUpdateData.preferenciaNota = comNota ? 'com_nota' : 'sem_nota';
            }

            if (vhsysNfseId) {
                ticketUpdateData.vhsysNfseId = vhsysNfseId;
                ticketUpdateData.vhsysRpsNumero = vhsysRpsNumero;
            }
            if (vhsysReceitaId) {
                ticketUpdateData.vhsysReceitaId = vhsysReceitaId;
            }

            setSelectedTicket(prev => ({
                ...prev,
                ...ticketUpdateData
            }));

            await updateDoc(doc(db, paths.tickets, selectedTicket.id), ticketUpdateData);
            
            let commentMsg = `**Chamado Finalizado (${type === 'contrato' ? 'Contrato' : 'Avulso'})**`;
            if (vhsysNfseId) {
                commentMsg += `\n**Nota Fiscal (NFS-e):** Rascunho gerado no VHSYS (RPS nº ${vhsysRpsNumero || vhsysNfseId}) - Aguardando conferência e emissão`;
            } else if (vhsysReceitaId) {
                commentMsg += `\n**Faturamento no VHSYS:** Lançado em Contas a Receber (Receita ID ${vhsysReceitaId})`;
            } else if (type === 'avulso' && FATURAMENTO_PELO_FINANCEIRO) {
                commentMsg += `\n**Cobrança:** enviada ao Financeiro para faturamento (cliente ${comNota ? 'quer' : 'não quer'} nota fiscal)`;
            }
            if (selectedTicket.closeNotes) {
                commentMsg += `\n**Observações:** ${selectedTicket.closeNotes}`;
            }
            await handleSendComment(true, commentMsg);
            
            // Disparar e-mail de notificação de conclusão
            if (selectedTicket.clientEmail) {
                try {
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const sendCommentEmailFn = httpsCallable(functions, 'enviarEmailComentario');
                    await sendCommentEmailFn({
                        ticketNumber: selectedTicket.numeroSequencial,
                        ticketTitle: selectedTicket.title,
                        clientName: selectedTicket.clientName,
                        clientEmail: selectedTicket.clientEmail,
                        commentText: `Olá! Passando para informar que o seu chamado de número A${selectedTicket.numeroSequencial} com o título "${selectedTicket.title}" foi finalizado com sucesso por ${auth.currentUser.displayName || auth.currentUser.email}.`
                    });
                } catch (err) {
                    console.error("Erro ao enviar e-mail de encerramento:", err);
                }
            }

            setSuccessMessage(vhsysReceitaId ? "Chamado Finalizado e Faturado com Sucesso!" : "Chamado Finalizado com Sucesso!");
            setIsCloseTicketModalOpen(false);
        } catch (error) {
            console.error("Erro ao fechar chamado:", error);
            alert("Erro ao fechar chamado: " + error.message);
        } finally {
            setBillingLoading(false);
        }
    };

    const handleSaveSchedule = async () => {
        if (!selectedTicket || !tempScheduleDate || !tempScheduleTime) return;
        try {
            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                isScheduled: true,
                scheduledDate: tempScheduleDate,
                scheduledTime: tempScheduleTime,
                scheduledType: tempScheduleType
            });
            await handleSendComment(true, `**Chamado Agendado (${tempScheduleType})** para o dia **${tempScheduleDate.split('-').reverse().join('/')}** às **${tempScheduleTime}**`);
            setSelectedTicket(prev => ({
                ...prev,
                isScheduled: true,
                scheduledDate: tempScheduleDate,
                scheduledTime: tempScheduleTime,
                scheduledType: tempScheduleType
            }));
            setIsScheduleModalOpen(false);
        } catch (err) {
            console.error("Erro ao salvar agendamento:", err);
        }
    };

    const handleRemoveSchedule = async () => {
        if (!selectedTicket) return;
        try {
            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                isScheduled: false,
                scheduledDate: '',
                scheduledTime: '',
                scheduledType: ''
            });
            await handleSendComment(true, `**Agendamento Removido**`);
            setSelectedTicket(prev => {
                const updated = { ...prev };
                delete updated.isScheduled;
                delete updated.scheduledDate;
                delete updated.scheduledTime;
                delete updated.scheduledType;
                return updated;
            });
            setIsScheduleModalOpen(false);
        } catch (err) {
            console.error("Erro ao remover agendamento:", err);
        }
    };

    const handleAddServiceToTicket = async () => {
        if (!selectedTicket) return;
        
        let newServiceItem;
        if (isCustomService) {
            if (!customServiceName.trim() || !customServiceValue) return;
            let valStr = String(customServiceValue).replace('R$', '').trim();
            const valueNum = parseFloat(valStr.replace(',', '.')) || 0;
            const qty = parseInt(serviceQuantity) || 1;
            newServiceItem = {
                id: `custom_${Date.now()}`,
                name: customServiceName.trim() + " (Não Listado)",
                value: valueNum,
                quantity: qty,
                total: valueNum * qty
            };
        } else {
            if (!selectedServiceId) return;
            const service = servicesPriceTable.find(s => s.id === selectedServiceId);
            if (!service) return;
            
            let serviceValue = service.value;
            if (selectedTicket.priceTableSnapshot && selectedTicket.priceTableSnapshot[service.id] !== undefined) {
                serviceValue = selectedTicket.priceTableSnapshot[service.id];
            }
            
            let valStr = String(serviceValue || '0').replace('R$', '').trim();
            const valueNum = parseFloat(valStr.replace(',', '.')) || 0;
            const qty = parseInt(serviceQuantity) || 1;
            newServiceItem = {
                id: service.id,
                name: service.name,
                value: valueNum,
                quantity: qty,
                total: valueNum * qty
            };
        }

        const currentServices = [...(selectedTicket.services || [])];
        currentServices.push(newServiceItem);

        const currentDiscount = selectedTicket.discountPercent || 0;
        const subtotal = currentServices.reduce((sum, s) => sum + s.total, 0);
        const totalAmount = subtotal * (1 - currentDiscount / 100);

        // Lock prices by generating/updating the snapshot of the current price table
        const priceTableSnapshot = selectedTicket.priceTableSnapshot || {};
        if (Object.keys(priceTableSnapshot).length === 0) {
            servicesPriceTable.forEach(item => {
                priceTableSnapshot[item.id] = item.value;
            });
        }

        try {
            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                services: currentServices,
                totalAmount: totalAmount,
                priceTableSnapshot: priceTableSnapshot
            });
            setSelectedTicket(prev => ({
                ...prev,
                services: currentServices,
                totalAmount: totalAmount,
                priceTableSnapshot: priceTableSnapshot
            }));
            setSelectedServiceId('');
            setServiceQuantity(1);
            setServiceSearchText('');
            setCustomServiceName('');
            setCustomServiceValue('');
            setIsCustomService(false);
        } catch (error) {
            console.error("Erro ao adicionar serviço:", error);
            alert("Erro ao adicionar serviço: " + error.message);
        }
    };

    const handleRemoveServiceFromTicket = async (index) => {
        if (!selectedTicket) return;
        const currentServices = [...(selectedTicket.services || [])];
        currentServices.splice(index, 1);

        const currentDiscount = selectedTicket.discountPercent || 0;
        const subtotal = currentServices.reduce((sum, s) => sum + s.total, 0);
        const totalAmount = subtotal * (1 - currentDiscount / 100);

        try {
            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                services: currentServices,
                totalAmount: totalAmount
            });
            setSelectedTicket(prev => ({
                ...prev,
                services: currentServices,
                totalAmount: totalAmount
            }));
        } catch (error) {
            console.error("Erro ao remover serviço:", error);
            alert("Erro ao remover serviço: " + error.message);
        }
    };

    const handleUpdateDiscount = async (percent) => {
        if (!selectedTicket) return;
        const currentServices = selectedTicket.services || [];
        const subtotal = currentServices.reduce((sum, s) => sum + s.total, 0);
        const totalAmount = subtotal * (1 - percent / 100);

        try {
            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                discountPercent: percent,
                totalAmount: totalAmount
            });
            setSelectedTicket(prev => ({
                ...prev,
                discountPercent: percent,
                totalAmount: totalAmount
            }));
        } catch (error) {
            console.error("Erro ao atualizar desconto:", error);
            alert("Erro ao atualizar desconto: " + error.message);
        }
    };

    const handleSendComment = async (isInternal = false, textOverride = null) => {
        const textToSend = textOverride || newComment;
        if (!textToSend.trim() || !selectedTicket) return;
        
        await addDoc(collection(db, `${paths.tickets}/${selectedTicket.id}/comments`), {
            text: textToSend,
            userName: auth.currentUser?.displayName || (auth.currentUser?.email ? auth.currentUser.email.split('@')[0] : 'Técnico'),
            userEmail: auth.currentUser?.email || '',
            createdAt: serverTimestamp(),
            isInternal: isInternal
        });

        // Limpa a flag de resposta do cliente pois o técnico está interagindo
        await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
            clientReplied: false
        });
        
        if (!isInternal && selectedTicket.clientEmail) {
            try {
                const { getFunctions, httpsCallable } = await import('firebase/functions');
                const functions = getFunctions();
                const sendCommentEmailFn = httpsCallable(functions, 'enviarEmailComentario');
                
                await sendCommentEmailFn({
                    ticketNumber: selectedTicket.numeroSequencial,
                    ticketTitle: selectedTicket.title,
                    clientName: selectedTicket.clientName,
                    clientEmail: selectedTicket.clientEmail,
                    commentText: textToSend
                });
            } catch (err) {
                console.error("Erro ao enviar email de comentário:", err);
            }
        }

        if (!textOverride) setNewComment('');
    };

    const handleDeleteAttachment = async (indexToDelete) => {
        const attachment = newTicket.attachments[indexToDelete];
        if (attachment.path) {
            try {
                const { ref, deleteObject } = await import('firebase/storage');
                const fileRef = ref(storage, attachment.path);
                await deleteObject(fileRef);
            } catch (err) {
                console.error("Erro ao deletar arquivo do storage:", err);
            }
        }
        const updated = (newTicket.attachments || []).filter((_, idx) => idx !== indexToDelete);
        setNewTicket({ ...newTicket, attachments: updated });
    };

    const handleSaveTicket = async () => {
        if (!newTicket.title.trim()) return;
        setUploading(true);
        try {
            let uploadedAttachments = [...(newTicket.attachments || [])];

            // 1. Fazer upload dos novos arquivos se existirem
            if (selectedFiles.length > 0) {
                const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
                const ticketId = formMode === 'edit' && selectedTicket ? selectedTicket.id : doc(collection(db, paths.tickets)).id;

                for (const file of selectedFiles) {
                    const storageRef = ref(storage, `tickets/${ticketId}/${Date.now()}_${file.name}`);
                    const uploadResult = await uploadBytes(storageRef, file);
                    const downloadUrl = await getDownloadURL(uploadResult.ref);
                    uploadedAttachments.push({
                        name: file.name,
                        url: downloadUrl,
                        type: file.type,
                        path: storageRef.fullPath
                    });
                }
            }

            const finalTicketData = {
                ...newTicket,
                attachments: uploadedAttachments
            };

            if (formMode === 'edit' && selectedTicket) {
                const { id, createdAt, numeroSequencial, ...dataToUpdate } = finalTicketData;
                await updateDoc(doc(db, paths.tickets, selectedTicket.id), dataToUpdate);
            } else {
                const ticketId = doc(collection(db, paths.tickets)).id;
                const finalId = selectedFiles.length > 0 ? (uploadedAttachments[0].path.split('/')[1]) : ticketId;
                const newTicketDocRef = doc(db, paths.tickets, finalId);

                await runTransaction(db, async (transaction) => {
                    const counterDoc = await transaction.get(doc(db, paths.counter));
                    const newId = (counterDoc.data()?.currentId || 0) + 1;
                    transaction.set(newTicketDocRef, {
                        ...finalTicketData,
                        numeroSequencial: newId, 
                        status: 'Novo Chamado',
                        createdByEmail: auth.currentUser.email,
                        createdAt: serverTimestamp()
                    });
                    transaction.update(doc(db, paths.counter), { currentId: newId });
                });
            }

            setIsFormOpen(false);
            setSelectedFiles([]);
            setNewTicket({ title: '', description: '', priority: 'low', assignedTo: [], assignedToEmail: [], status: '', clientName: '', clientEmail: '', clientWhatsApp: '', clientCompany: '', attachments: [] });
        } catch (error) { 
            console.error("Erro ao salvar chamado:", error); 
        } finally {
            setUploading(false);
        }
    };

    const uploadFilesDirectly = async (files) => {
        if (!files || files.length === 0 || !selectedTicket) return;
        setUploadingDirect(true);
        try {
            const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
            let uploadedAttachments = [...(selectedTicket.attachments || [])];

            for (const file of files) {
                const storageRef = ref(storage, `tickets/${selectedTicket.id}/${Date.now()}_${file.name}`);
                const uploadResult = await uploadBytes(storageRef, file);
                const downloadUrl = await getDownloadURL(uploadResult.ref);
                uploadedAttachments.push({
                    name: file.name,
                    url: downloadUrl,
                    type: file.type,
                    path: storageRef.fullPath
                });
            }

            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                attachments: uploadedAttachments
            });

            const filesListStr = files.map(f => `\`${f.name}\``).join(', ');
            await handleSendComment(true, `**Novos arquivos anexados:** ${filesListStr}`);

        } catch (error) {
            console.error("Erro no upload direto:", error);
        } finally {
            setUploadingDirect(false);
        }
    };

    const handleDirectUpload = (e) => {
        if (e.target.files) {
            uploadFilesDirectly(Array.from(e.target.files));
        }
    };

    const handlePasteInComment = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const filesToUpload = [];
        for (const item of items) {
            if (item.type.indexOf("image") !== -1) {
                const file = item.getAsFile();
                if (file) {
                    e.preventDefault();
                    const extension = file.type.split('/')[1] || 'png';
                    const renamedFile = new File([file], `Imagem_Colada_${Date.now()}.${extension}`, { type: file.type });
                    filesToUpload.push(renamedFile);
                }
            }
        }

        if (filesToUpload.length > 0) {
            uploadFilesDirectly(filesToUpload);
        }
    };

    const handleSelectClient = (client) => {
        setNewTicket(prev => ({ 
            ...prev, 
            clientName: client.name, 
            clientEmail: client.email, 
            clientWhatsApp: client.whatsapp || '', 
            clientCompany: client.company || '',
            clientCompanyId: client.id // Gravamos o ID para facilitar o filtro futuro
        }));
        setClientSearch('');
        setShowClientResults(false);
    };

    return (
        <div className="relative pt-8 md:pt-12 px-4 md:px-8 pb-8 font-sans text-white bg-slate-900 min-h-screen print:bg-white print:text-black">
            
            <StatusManagerModal 
                isOpen={isStatusManagerOpen} 
                onClose={() => setIsStatusManagerOpen(false)} 
                statuses={statuses} 
                onAdd={d => addDoc(collection(db, paths.statuses), d)} 
                onUpdate={(id, d) => updateDoc(doc(db, paths.statuses, id), d)}
                onDelete={id => deleteDoc(doc(db, paths.statuses, id))} 
            />

            <LocalConfirmModal 
                isOpen={!!templateToRemove} title="Remover Informação?" message="Deseja remover esta categoria deste chamado? Os dados já preenchidos não serão apagados do banco de dados."
                onConfirm={async () => {
                    const added = (selectedTicket.addedTemplates || []).filter(id => id !== templateToRemove);
                    await updateDoc(doc(db, paths.tickets, selectedTicket.id), { addedTemplates: added });
                    setTemplateToRemove(null);
                }}
                onCancel={() => setTemplateToRemove(null)}
            />

            <LocalConfirmModal 
                isOpen={!!ticketToReopen} title="Reabrir Chamado?" message="Deseja realmente reabrir este chamado?"
                onConfirm={async () => {
                    if (!ticketToReopen) return;
                    try {
                        await updateDoc(doc(db, paths.tickets, ticketToReopen.id), {
                            status: 'Novo Chamado'
                        });
                        await addDoc(collection(db, `${paths.tickets}/${ticketToReopen.id}/comments`), {
                            text: `**Chamado Reaberto**`,
                            userEmail: auth.currentUser.email,
                            createdAt: serverTimestamp(),
                            isInternal: true
                        });
                    } catch (err) {
                        console.error("Erro ao reabrir chamado:", err);
                    } finally {
                        setTicketToReopen(null);
                    }
                }}
                onCancel={() => setTicketToReopen(null)}
            />

            {viewMode === 'detail' && selectedTicket ? (
                /* --- VISÃO DETALHADA --- */
                <>
                <div className="w-full max-w-none animate-fadeIn flex flex-col h-auto font-sans">
                    <div className="relative z-50 mb-6 flex-shrink-0">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <button onClick={() => {
                            setViewMode('list');
                            setSelectedTicket(null);
                            setReturnToFinanceiroId(null);
                            if (cameFromFinishedModal) {
                                setIsFinishedModalOpen(true);
                                setCameFromFinishedModal(false);
                            }
                        }} className="text-cyan-400 hover:text-cyan-300 flex items-center gap-2 font-bold transition-colors print:hidden font-sans">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Voltar para a Lista
                        </button>
                        {returnToFinanceiroId && returnToFinanceiroId === selectedTicket.id && navigateTo && (
                            <button onClick={() => {
                                try { sessionStorage.setItem('arcus_open_cobranca_id', returnToFinanceiroId); } catch (e) {}
                                setReturnToFinanceiroId(null);
                                navigateTo('financeiro');
                            }} className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2 font-bold transition-colors print:hidden font-sans">
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                Voltar para a Cobrança
                            </button>
                        )}
                        </div>
                    </div>

                    <header className="relative z-40 flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b border-slate-800 pb-6 flex-shrink-0 print:border-black font-sans gap-4">
                        <div className="flex-1 font-sans w-full">
                            <h1 className="text-3xl md:text-5xl font-bold text-cyan-400 print:text-black leading-tight">A{selectedTicket.numeroSequencial} - {selectedTicket.title}</h1>
                            <div className="mt-4 flex flex-col md:flex-row md:gap-6 gap-2 text-sm md:items-center font-bold uppercase tracking-wider font-sans">
                                <div className="flex gap-2 items-baseline">
                                    <span className="text-slate-500 font-sans text-[10px]">CLIENTE:</span>
                                    <span className="text-white">{selectedTicket.clientName}</span>
                                </div>
                                <div className="flex gap-2 items-baseline">
                                    <span className="text-slate-500 font-sans text-[10px]">EMPRESA:</span>
                                    <span className="text-white">{getCompanyDisplayName(selectedTicket.clientCompanyId || selectedTicket.clientCompany)}</span>
                                </div>
                                <div className="flex gap-2 items-baseline">
                                    <span className="text-slate-500 font-sans text-[10px]">ID DOC:</span>
                                    <span className="text-slate-400 font-mono text-xs select-all" title="Firebase Document ID">{selectedTicket.id}</span>
                                </div>
                                <div className="mt-1 md:mt-0 flex gap-2 items-center flex-wrap">
                                    {selectedTicket.contractName && (
                                        <span className={`text-[10px] px-2.5 py-0.5 rounded-full border font-bold uppercase tracking-wider font-sans flex items-center gap-1 ${
                                            selectedTicket.contractName === 'Outros'
                                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                                : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                        }`}>
                                            📄 Contrato: {selectedTicket.contractName}
                                        </span>
                                    )}
                                    {selectedTicket.isScheduled && (
                                        <span className="text-[9px] px-2 py-0.5 rounded border border-emerald-500/20 bg-emerald-600/20 text-emerald-400 font-black uppercase tracking-wider font-sans">
                                            Agendado ({selectedTicket.scheduledType || 'Remoto'}): {selectedTicket.scheduledDate?.split('-').reverse().slice(0, 2).join('/')} às {selectedTicket.scheduledTime}
                                        </span>
                                    )}
                                    <span className="px-2 py-0.5 rounded border border-white/10 text-[10px] whitespace-nowrap" style={{ backgroundColor: statuses.find(s => s.name === selectedTicket.status)?.color || (isFinished(selectedTicket.status) ? (selectedTicket.status?.includes('Contrato') ? '#10b981' : '#06b6d4') : '#000') }}>{selectedTicket.status}</span>
                                </div>
                            </div>
                            <div className="mt-6 text-slate-300 text-base md:text-lg italic leading-relaxed border-l-4 border-cyan-500/30 pl-4 print:text-black whitespace-pre-wrap font-sans">{selectedTicket.description}</div>
                            
                            {selectedTicket.closeNotes && (
                                <div className="mt-6 bg-slate-800/60 border border-slate-700/60 rounded-xl p-4 text-slate-300 font-sans text-sm leading-relaxed whitespace-pre-wrap">
                                    <strong className="block text-[10px] font-bold uppercase tracking-wider text-cyan-400 mb-1">Observações do Fechamento / Pagamento:</strong>
                                    {selectedTicket.closeNotes}
                                </div>
                            )}
                             
                             {/* Anexos removidos daqui para o rodapé */}
                        </div>
                        <div className="flex flex-col items-stretch md:items-end gap-2 print:hidden relative z-50 font-sans w-full md:w-auto mt-2 md:mt-0">
                            {/* Mostra o total acumulado do chamado se houver */}
                            {selectedTicket.totalAmount > 0 && (
                                <div className="w-full bg-slate-800/40 border border-slate-700/40 rounded-lg p-2.5 flex justify-between items-center text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                    <span className="flex items-center gap-2">
                                        <svg className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Total Cobrado:
                                    </span>
                                    <span className="text-emerald-400 font-mono font-bold text-xs">
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket.totalAmount || 0)}
                                    </span>
                                </div>
                            )}

                            {/* ROW DE BOTÕES DE CONTROLE */}
                            <div className="flex flex-wrap gap-2 md:gap-3 items-center justify-end w-full">
                                 {isAllowed('manage-status') && (
                                    <button onClick={() => { setTempStatus(selectedTicket.status); setActiveModal('status'); }} className="flex-1 md:flex-none bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded text-[10px] font-bold uppercase transition-all font-sans whitespace-nowrap">STATUS</button>
                                 )}
                                 {isAllowed('assign-technician') && (
                                    <button onClick={() => setActiveModal('assign')} className="flex-1 md:flex-none bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded text-[10px] font-bold uppercase transition-all font-sans whitespace-nowrap">ATRIBUIR</button>
                                 )}
                                 {isAllowed('edit-ticket') && (
                                    <button onClick={() => {
                                        setTempScheduleDate(selectedTicket.scheduledDate || '');
                                        setTempScheduleTime(selectedTicket.scheduledTime || '');
                                        setTempScheduleType(selectedTicket.scheduledType || 'Remoto');
                                        setIsScheduleModalOpen(true);
                                    }} className="flex-1 md:flex-none bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded text-[10px] font-bold uppercase transition-all font-sans whitespace-nowrap text-emerald-400 hover:text-emerald-300">AGENDAMENTO</button>
                                 )}
                                  {!FATURAMENTO_PELO_FINANCEIRO && selectedTicket.status === 'Finalizado Avulso' && (selectedTicket.totalAmount || 0) > 0 && (
                                     <button 
                                         onClick={() => {
                                             setBillingDueDate(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                                             setIsBillingModalOpen(true);
                                         }} 
                                         className="flex-1 md:flex-none bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded text-[10px] font-bold uppercase transition-all font-sans whitespace-nowrap text-white"
                                     >
                                         Faturar
                                     </button>
                                  )}
                                  {!isFinished(selectedTicket.status) && (
                                     <button 
                                         onClick={() => {
                                             setCloseCompanyConfirmed(false);
                                             setCloseSelectedCompanyId(selectedTicket.clientCompanyId || selectedTicket.clientCompany || '');
                                             setIsCloseTicketModalOpen(true);
                                         }} 
                                         className="flex-1 md:flex-none bg-red-600 hover:bg-red-500 px-4 py-2 rounded text-[10px] font-bold uppercase transition-all font-sans whitespace-nowrap text-white"
                                     >
                                         Fechar Chamado
                                     </button>
                                  )}
                                 <div className="flex gap-1">
                                     {isAllowed('edit-ticket') && (
                                         <button onClick={() => { setFormMode('edit'); setNewTicket(selectedTicket); setIsFormOpen(true); }} className="p-2 text-slate-400 hover:text-cyan-400 transition-colors font-sans"><svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg></button>
                                     )}
                                     <div className="relative">
                                         <button onClick={() => setIsPrintMenuOpen(v => !v)} className="p-2 text-slate-400 hover:text-white transition-colors font-sans" title="Imprimir relatório do chamado"><svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg></button>
                                         {isPrintMenuOpen && (
                                             <>
                                                 <div className="fixed inset-0 z-40" onClick={() => setIsPrintMenuOpen(false)}></div>
                                                 <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-fadeIn">
                                                     <button onClick={() => handlePrintTicket(false)} className="w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors">
                                                         <span className="block text-sm font-bold text-white">Relatório para o cliente</span>
                                                         <span className="block text-[11px] text-slate-400">Sem os comentários internos</span>
                                                     </button>
                                                     <button onClick={() => handlePrintTicket(true)} className="w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors border-t border-slate-700">
                                                         <span className="block text-sm font-bold text-white">Relatório completo</span>
                                                         <span className="block text-[11px] text-slate-400">Inclui os comentários internos</span>
                                                     </button>
                                                 </div>
                                             </>
                                         )}
                                     </div>
                                 </div>
                            </div>
                        </div>
                    </header>

                    <DocumentosFiscais ticketId={selectedTicket.id} className="-mt-2 mb-6" />

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-4 font-sans">
                        <div className="lg:col-span-2 bg-slate-800/40 rounded-xl border border-slate-700 p-6 flex flex-col h-[550px] shadow-inner">
                            <h2 className="text-xl font-bold text-cyan-300 mb-6 flex-shrink-0 uppercase tracking-widest font-sans">Comentários</h2>
                            <div className="flex-grow md:overflow-y-auto space-y-4 mb-4 pr-2 custom-scrollbar font-sans">
                                {comments.length === 0 ? (
                                    <p className="text-slate-500 text-sm italic text-center py-8">Nenhum comentário ainda.</p>
                                ) : [...comments].reverse().map(c => (
                                    <div 
                                        key={c.id} 
                                        className={`p-4 rounded-lg border shadow-sm animate-fadeIn transition-all ${
                                            c.isInternal 
                                                ? 'bg-amber-500/10 border-amber-500/30' 
                                                : 'bg-slate-800/80 border-slate-700'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center mb-1">
                                            <span className={`text-[11px] font-bold ${c.isInternal ? 'text-amber-400' : 'text-cyan-400'}`}>
                                                {c.userName ? `${c.userName} (${c.userEmail})` : (c.userEmail || 'Sistema Arcus')} {c.isInternal && <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded font-black ml-1 uppercase">INTERNO</span>}
                                            </span>
                                            <span className="text-[9px] text-slate-500 uppercase">
                                                {c.createdAt ? c.createdAt.toDate().toLocaleString() : 'Enviando...'}
                                            </span>
                                        </div>
                                        <p className="text-slate-300 text-sm whitespace-pre-wrap">{c.text}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 font-sans items-center print:hidden">
                                 <textarea value={newComment} onChange={e => setNewComment(e.target.value)} onPaste={handlePasteInComment} placeholder="Adicionar comentário..." className="flex-1 bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 text-sm font-sans" rows="2" />
                                 <div className="flex gap-2 w-full sm:w-auto shrink-0 justify-end items-center">
                                     {isAllowed('edit-ticket') && (
                                         <label className="cursor-pointer bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-cyan-500/30 h-10 w-10 sm:h-12 sm:w-12 rounded-lg flex items-center justify-center transition-all shadow-lg select-none text-cyan-400">
                                             {uploadingDirect ? (
                                                 <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400"></div>
                                             ) : (
                                                 <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                     <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                 </svg>
                                             )}
                                             <input 
                                                 type="file" 
                                                 multiple 
                                                 disabled={uploadingDirect}
                                                 onChange={handleDirectUpload}
                                                 className="hidden" 
                                             />
                                         </label>
                                     )}
                                     <button onClick={() => { if (newComment.trim()) setIsCommentModalOpen(true); }} className="bg-cyan-500 hover:bg-cyan-600 px-6 h-10 sm:h-12 rounded-lg font-bold transition-all shadow-lg font-sans text-sm flex items-center justify-center">Enviar</button>
                                 </div>
                             </div>
                        </div>

                        <div className="bg-slate-800/40 rounded-xl border border-slate-700 p-6 flex flex-col h-[550px] shadow-inner font-sans">
                            <div className="relative z-50 flex justify-between items-center mb-6 flex-shrink-0 font-sans font-sans">
                                <h2 className="text-xl font-bold text-cyan-300 uppercase tracking-widest font-sans font-sans">Informações</h2>
                                {isAllowed('add-technical-category') && (
                                    <button onClick={() => setActiveModal('addTemplate')} className="bg-cyan-500 rounded-full h-8 w-8 hover:scale-110 transition-transform shadow-lg print:hidden flex items-center justify-center font-bold text-xl">+</button>
                                )}
                            </div>
                            <div className="flex-grow overflow-y-auto space-y-4 pr-1 custom-scrollbar font-sans">
                                {launchTemplates.filter(t => (selectedTicket.addedTemplates || []).includes(t.id)).map(template => (
                                    <div key={template.id} className="bg-slate-800 p-5 rounded-xl border border-slate-700 shadow-lg print:border-black font-sans group">
                                        <div className="flex justify-between items-center border-b border-slate-700 pb-2 mb-4 font-sans font-sans">
                                            <h3 className="font-bold text-xs text-cyan-300 uppercase tracking-widest font-sans">{template.name}</h3>
                                            {/* ÍCONE DE REMOVER CATEGORIA */}
                                            {isAllowed('add-technical-category') && (
                                                <button onClick={() => setTemplateToRemove(template.id)} className="text-slate-600 hover:text-red-500 transition-colors">
                                                    <svg className="h-4 w-4 font-sans" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-4 font-sans font-sans">{template.fields?.map((field, idx) => (
                                            <div key={idx} className="flex flex-col font-sans">
                                                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter font-sans">{field.name}</span>
                                                {isAllowed('edit-technical-data') ? (
                                                    <>
                                                        <button 
                                                            onClick={() => { 
                                                                setTempLaunchData({ 
                                                                    templateId: template.id, 
                                                                    templateName: template.name, 
                                                                    fieldName: field.name, 
                                                                    fieldType: field.type || 'text',
                                                                    fieldOptions: field.options || '',
                                                                    value: selectedTicket.technicalData?.[template.id]?.[field.name] || '' 
                                                                }); 
                                                                setActiveModal('launch'); 
                                                            }} 
                                                            className="text-left text-slate-300 text-xs hover:text-cyan-400 italic font-sans print:hidden"
                                                        >
                                                            {selectedTicket.technicalData?.[template.id]?.[field.name] || 'Clique para lançar...'}
                                                        </button>
                                                        <span className="hidden print:block text-black text-xs font-sans">
                                                            {selectedTicket.technicalData?.[template.id]?.[field.name] || 'Nenhum dado lançado'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-slate-500 text-xs italic font-sans">
                                                        {selectedTicket.technicalData?.[template.id]?.[field.name] || 'Nenhum dado lançado'}
                                                    </span>
                                                )}
                                            </div>
                                        ))}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* --- SEÇÃO DE SERVIÇOS E CUSTOS (VISÍVEL NO DETALHE E NA IMPRESSÃO) --- */}
                    {selectedTicket.services && selectedTicket.services.length > 0 && (
                        <div className="mt-6 bg-slate-800/40 rounded-xl border border-slate-700 p-6 flex-shrink-0 font-sans page-break-inside-avoid">
                            <h3 className="text-xs font-bold text-cyan-300 uppercase tracking-widest mb-4 print:text-black">Serviços Executados & Valores</h3>
                            <div className="overflow-hidden border border-slate-700/60 rounded-lg bg-slate-900/40 print:border-slate-300">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="bg-slate-800/80 text-slate-400 border-b border-slate-700 font-bold uppercase tracking-wider print:bg-slate-100 print:text-black">
                                            <th className="p-3">Serviço</th>
                                            <th className="p-3 text-right">Unitário</th>
                                            <th className="p-3 text-center">Quant.</th>
                                            <th className="p-3 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/40 text-slate-200 print:divide-slate-200">
                                        {selectedTicket.services.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                                                <td className="p-3 font-semibold text-slate-300 print:text-black">{item.name}</td>
                                                <td className="p-3 text-right font-mono print:text-black">
                                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.value || 0)}
                                                </td>
                                                <td className="p-3 text-center font-bold font-mono print:text-black">{item.quantity}</td>
                                                <td className="p-3 text-right font-bold font-mono text-emerald-400 print:text-black">
                                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total || 0)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            <div className="mt-4 flex flex-col items-end gap-1.5 font-sans pr-2">
                                {(selectedTicket.discountPercent || 0) > 0 && (
                                    <span className="text-[10px] text-slate-500 line-through font-mono leading-none print:text-slate-600">
                                        Subtotal: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket.services.reduce((sum, s) => sum + s.total, 0) || 0)}
                                    </span>
                                )}
                                {(selectedTicket.discountPercent || 0) > 0 && (
                                    <span className="text-[10px] text-cyan-400 font-bold font-mono leading-none print:text-black">
                                        Desconto: {selectedTicket.discountPercent}%
                                    </span>
                                )}
                                <div className="flex items-baseline gap-2 mt-1">
                                    <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold print:text-black">Valor Total Cobrado:</span>
                                    <span className="text-xl font-bold font-mono text-emerald-400 print:text-black">
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket.totalAmount || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- SEÇÃO DE ANEXOS (RODAPÉ DO CHAMADO) --- */}
                    {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                        <div className="mt-6 bg-slate-800/40 rounded-xl border border-slate-700 p-6 flex-shrink-0 font-sans">
                            <h3 className="text-xs font-bold text-cyan-300 uppercase tracking-widest mb-4">Anexos ({selectedTicket.attachments.length})</h3>
                            <div className="flex flex-wrap gap-4">
                                {selectedTicket.attachments.map((file, idx) => {
                                    const isImg = file.type?.startsWith('image/');
                                    return (
                                        <div key={idx} className="flex flex-col items-center">
                                            {isImg ? (
                                                <button 
                                                    onClick={() => setSelectedImageModal(file.url)}
                                                    className="w-20 h-20 rounded-lg overflow-hidden border border-slate-700 hover:border-cyan-400 transition-all flex items-center justify-center bg-slate-900 shadow-lg group relative"
                                                >
                                                    <img src={file.url} alt={file.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                                    </div>
                                                </button>
                                            ) : (
                                                <a 
                                                    href={file.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="w-20 h-20 rounded-lg border border-slate-700 hover:border-cyan-400 transition-all flex flex-col items-center justify-center bg-slate-900 shadow-lg group text-cyan-400"
                                                >
                                                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </a>
                                            )}
                                            <span className="text-[9px] text-slate-500 mt-1.5 max-w-[90px] truncate text-center font-medium" title={file.name}>{file.name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* --- MODAL ZOOM DE IMAGEM ANEXADA --- */}
                {selectedImageModal && (
                    <div 
                        className="fixed inset-0 bg-black/90 flex justify-center items-center z-[200] p-4 animate-fadeIn cursor-zoom-out"
                        onClick={() => setSelectedImageModal(null)}
                    >
                        <div className="relative max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                            <button 
                                onClick={() => setSelectedImageModal(null)} 
                                className="absolute -top-12 right-0 text-white hover:text-cyan-400 text-3xl font-bold bg-black/50 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                            >
                                ✕
                            </button>
                            <img 
                                src={selectedImageModal} 
                                alt="Visualização do anexo" 
                                className="max-w-full max-h-[85vh] object-contain rounded-lg border border-white/10 shadow-2xl" 
                            />
                        </div>
                    </div>
                )}
                </>
            ) : (
                /* --- LISTAGEM COM ENGRENAGEM --- */
                <>
                    <header className="mb-8 flex justify-between items-center flex-wrap gap-4 font-sans">
                        <div><h1 className="text-5xl font-bold text-cyan-400 font-sans">Chamados</h1><p className="text-slate-400 mt-2 font-sans font-sans">Gerencie os atendimentos da Arcus.</p></div>
                        <div className="flex items-center gap-4 flex-wrap font-sans">
                            {isAllowed('filter-search') && (
                                <input type="text" placeholder="Pesquisar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md p-3 text-white w-64 shadow-inner outline-none font-sans" />
                            )}
                            {isAllowed('filter-status') && (
                                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md p-2 text-white shadow-inner outline-none font-sans font-sans"><option value="all">Todos Status</option>{statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select>
                            )}
                            {isAllowed('filter-status') && (
                                <select value={filterScheduled} onChange={e => setFilterScheduled(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md p-2 text-white shadow-inner outline-none font-sans"><option value="all">Todos Agendamentos</option><option value="scheduled">Apenas Agendados</option><option value="not_scheduled">Sem Agendamento</option></select>
                            )}
                            {isAllowed('filter-technician') && (
                                <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md p-2 text-white shadow-inner outline-none font-sans font-sans"><option value="all">Todos Técnicos</option>{users.sort((a,b) => a.email.localeCompare(b.email)).map(u => <option key={u.uid} value={u.uid}>{u.email}</option>)}</select>
                            )}
                            
                            <button 
                                onClick={() => setIsDashboardOpen(true)} 
                                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white rounded-md px-4 py-2.5 font-bold transition-all text-sm font-sans flex items-center gap-2"
                            >
                                <svg className="h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Dashboard
                            </button>

                            <button 
                                onClick={() => setIsFinishedModalOpen(true)} 
                                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white rounded-md px-4 py-2.5 font-bold transition-all text-sm font-sans flex items-center gap-2"
                            >
                                <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Finalizados
                            </button>

                            {/* ÍCONE DE ENGRENAGEM */}
                            {isAllowed('manage-settings') && (
                                <div className="relative font-sans" ref={manageDropdownRef}>
                                    <button onClick={() => setIsManageStatusDropdownOpen(!isManageStatusDropdownOpen)} className="p-2 text-slate-400 hover:text-white transition-colors font-sans">
                                        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </button>
                                    {isManageStatusDropdownOpen && (
                                        <div className="absolute right-0 mt-2 w-56 bg-slate-800 rounded-md shadow-2xl py-1 z-50 border border-slate-700 font-sans">
                                            <button onClick={() => { setIsStatusManagerOpen(true); setIsManageStatusDropdownOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-700 font-sans">Gerenciar Status</button>
                                            <button onClick={() => navigateTo('manage-ticket-info')} className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-700 font-sans font-sans">Cadastro de Informações</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </header>
                    <div className="bg-[#262d35] border border-[#3e4753] rounded-xl overflow-x-auto shadow-2xl font-sans">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead>
                                <tr className="border-b border-[#3e4753] bg-[#2d353f]">
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-20">Nº</th>
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Assunto / Descrição</th>
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-64">Solicitante</th>
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-32">Data</th>
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-36">Agendamento</th>
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-44">Status</th>
                                    <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-36">Técnico</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#3e4753]/80">
                                {filteredTickets.map((ticket) => {
                                    const statusColor = statuses.find(s => s.name === ticket.status)?.color || (isFinished(ticket.status) ? ((ticket.status?.includes('Contrato') || ticket.status?.includes('Sem Cobrança')) ? '#10b981' : '#06b6d4') : '#06b6d4');
                                    
                                    return (
                                    <tr 
                                        key={ticket.id} 
                                        onClick={() => handleOpenTicketDetail(ticket)} 
                                        className="hover:bg-[#353d48]/70 cursor-pointer transition-colors group"
                                    >
                                        <td className="py-5 px-6 align-top">
                                            <div className="flex flex-col items-center w-fit gap-2">
                                                <span className="text-cyan-400 font-bold text-sm">A{ticket.numeroSequencial}</span>
                                                {ticket.clientReplied && (
                                                    <span className="relative flex h-2.5 w-2.5" title="Novo comentário do cliente">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-5 px-6 align-top">
                                            <h3 className="text-[15px] font-bold text-white group-hover:text-cyan-300 transition-colors leading-tight mb-1">
                                                {ticket.title}
                                            </h3>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {ticket.contractName && (
                                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${
                                                        ticket.contractName === 'Outros'
                                                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                                                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                                                    }`}>
                                                        {ticket.contractName}
                                                    </span>
                                                )}
                                                <p className="text-slate-500 text-xs truncate max-w-md xl:max-w-xl">
                                                    {ticket.description || 'Sem descrição...'}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="py-5 px-6 align-top">
                                            <span className="text-slate-200 text-sm font-medium leading-snug block truncate max-w-[200px]" title={ticket.clientName}>
                                                {ticket.clientName || 'Sem Solicitante'}
                                            </span>
                                            {ticket.clientCompany && (
                                                <span className="text-slate-500 text-[10px] uppercase tracking-wider block mt-1 truncate max-w-[200px]" title={getCompanyDisplayName(ticket.clientCompanyId || ticket.clientCompany)}>
                                                    {getCompanyDisplayName(ticket.clientCompanyId || ticket.clientCompany)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-5 px-6 align-top">
                                            <span className="text-slate-300 text-sm">
                                                {ticket.createdAt ? ticket.createdAt.toDate().toLocaleDateString('pt-BR') : 'Sem data'}
                                            </span>
                                        </td>
                                        <td className="py-5 px-6 align-top">
                                            {ticket.isScheduled && ticket.scheduledDate ? (
                                                <div className="flex flex-col">
                                                    <span className="text-amber-400 font-bold text-xs uppercase tracking-wider">{ticket.scheduledDate.split('-').reverse().join('/')}</span>
                                                    <span className="text-slate-400 text-[10px] font-medium">{ticket.scheduledTime || 'Horário não definido'}</span>
                                                </div>
                                            ) : (
                                                <span className="text-slate-500 text-[10px] italic uppercase tracking-wider">Sem Agendamento</span>
                                            )}
                                        </td>
                                        <td className="py-5 px-6 align-top">
                                            <div 
                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-sm"
                                                style={{ 
                                                    backgroundColor: `${statusColor}15`, 
                                                    borderColor: `${statusColor}40`  
                                                }}
                                            >
                                                <div 
                                                    className="w-1.5 h-1.5 rounded-full"
                                                    style={{ backgroundColor: statusColor }}
                                                ></div>
                                                <span className="text-xs font-bold leading-none text-slate-200">
                                                    {ticket.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-5 px-6 align-top">
                                            {ticket.assignedToEmail && ticket.assignedToEmail.length > 0 ? (
                                                <span className="text-slate-300 text-sm leading-snug block">
                                                    {ticket.assignedToEmail[0].split('@')[0]}
                                                </span>
                                            ) : (
                                                <span className="text-slate-500 text-sm italic leading-snug block">
                                                    Sem<br/>atribuição
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* --- MODAIS --- */}
            {successMessage && (
                <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-[200] p-4 font-sans backdrop-blur-sm">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-sm p-6 flex flex-col items-center text-center shadow-2xl animate-fadeIn">
                        <div className="h-16 w-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4 border border-emerald-500/20 text-emerald-400">
                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h4 className="font-bold text-lg text-white mb-2 uppercase tracking-wider">Sucesso!</h4>
                        <p className="text-slate-300 text-sm mb-6 font-sans">{successMessage}</p>
                        <button 
                            onClick={() => {
                                setSuccessMessage('');
                                setViewMode('list');
                                setSelectedTicket(null);
                            }}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 px-4 rounded-lg text-xs transition-colors uppercase tracking-wider font-sans"
                        >
                            Ok
                        </button>
                    </div>
                </div>
            )}

            {isScheduleModalOpen && (
                <FormModal title="Agendamento do Chamado" onClose={() => setIsScheduleModalOpen(false)} onSave={handleSaveSchedule}>
                    <div className="space-y-4 font-sans p-2">
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Data do Agendamento</label>
                            <input 
                                type="date" 
                                value={tempScheduleDate} 
                                onChange={e => setTempScheduleDate(e.target.value)} 
                                className="w-full bg-slate-700 border border-slate-600 p-2.5 rounded text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans font-bold"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Hora do Agendamento</label>
                            <input 
                                type="time" 
                                value={tempScheduleTime} 
                                onChange={e => setTempScheduleTime(e.target.value)} 
                                className="w-full bg-slate-700 border border-slate-600 p-2.5 rounded text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans font-bold"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Tipo de Atendimento</label>
                            <select 
                                value={tempScheduleType} 
                                onChange={e => setTempScheduleType(e.target.value)} 
                                className="w-full bg-slate-700 border border-slate-600 p-2.5 rounded text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans font-bold"
                            >
                                <option value="Remoto">Remoto</option>
                                <option value="Local">Local</option>
                            </select>
                        </div>
                        {selectedTicket?.isScheduled && (
                            <button
                                type="button"
                                onClick={handleRemoveSchedule}
                                className="w-full mt-4 bg-red-950/40 hover:bg-red-900/40 border border-red-900/30 hover:border-red-500/30 text-red-400 text-[10px] font-black uppercase tracking-wider py-2.5 rounded transition-colors"
                            >
                                Remover Agendamento
                            </button>
                        )}
                    </div>
                </FormModal>
            )}

            {isLinkCompanyModalOpen && (
                <div className="fixed inset-0 bg-black/85 flex justify-center items-center z-[140] p-4 font-sans backdrop-blur-sm">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md p-6 shadow-2xl flex flex-col animate-fadeIn">
                        <header className="pb-4 border-b border-slate-700 mb-4 flex justify-between items-center">
                            <h3 className="font-bold text-lg text-cyan-300 uppercase tracking-wider">Vincular Empresa</h3>
                        </header>
                        <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                            {selectedTicket?.clientCompany?.toLowerCase().includes('smbot') 
                                ? 'Este chamado está vinculado a uma empresa não cadastrada ("SMBot"). Por favor, selecione a empresa correta para vinculá-lo e insira o protocolo (opcional).'
                                : 'Verifique a empresa vinculada a este chamado e informe o protocolo de atendimento se necessário.'}
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">Selecione a Empresa</label>
                                <select
                                    value={linkCompanySelectedId}
                                    onChange={e => setLinkCompanySelectedId(e.target.value)}
                                    className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans outline-none focus:ring-1 focus:ring-cyan-500 text-sm font-bold"
                                >
                                    <option value="">Selecione...</option>
                                    {dropdownCompanies.map(comp => (
                                        <option key={comp.id} value={comp.id}>{comp.displayName}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">Protocolo do Atendimento (Opcional)</label>
                                <input
                                    type="text"
                                    placeholder="Ex: 585381832"
                                    value={linkProtocolo}
                                    onChange={e => setLinkProtocolo(e.target.value)}
                                    className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans outline-none focus:ring-1 focus:ring-cyan-500 text-sm font-bold"
                                />
                            </div>
                            <label className="flex items-center gap-2.5 cursor-pointer pt-2 select-none">
                                <input 
                                    type="checkbox"
                                    checked={linkAutoAssign}
                                    onChange={e => setLinkAutoAssign(e.target.checked)}
                                    className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-cyan-500 focus:ring-0 focus:ring-offset-0 cursor-pointer accent-cyan-500"
                                />
                                <span className="text-xs text-slate-200 font-medium">
                                    Atribuir este chamado a mim automaticamente
                                </span>
                            </label>
                        </div>
                        <footer className="mt-8 pt-4 border-t border-slate-700 flex gap-4">
                            <button
                                type="button"
                                onClick={() => setIsLinkCompanyModalOpen(false)}
                                className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-bold py-2.5 rounded-lg text-xs uppercase tracking-wider transition-colors"
                            >
                                Ignorar
                            </button>
                            <button
                                type="button"
                                disabled={!linkCompanySelectedId}
                                onClick={async () => {
                                    const selectedComp = dropdownCompanies.find(c => c.id === linkCompanySelectedId);
                                    if (selectedComp && selectedTicket) {
                                        try {
                                            const updateData = {
                                                clientCompany: selectedComp.name,
                                                clientCompanyId: selectedComp.id
                                            };
                                            if (linkProtocolo.trim()) {
                                                updateData.protocolo = linkProtocolo.trim();
                                            }
                                            if (linkAutoAssign && auth.currentUser) {
                                                updateData.assignedTo = [...new Set([...(selectedTicket.assignedTo || []), auth.currentUser.uid])];
                                                updateData.assignedToEmail = [...new Set([...(selectedTicket.assignedToEmail || []), auth.currentUser.email])];
                                            }
                                            await updateDoc(doc(db, paths.tickets, selectedTicket.id), updateData);
                                            setSelectedTicket(prev => ({
                                                ...prev,
                                                ...updateData
                                            }));
                                            setIsLinkCompanyModalOpen(false);
                                            setLinkProtocolo('');
                                        } catch (err) {
                                            console.error("Erro ao vincular empresa:", err);
                                        }
                                    }
                                }}
                                className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-lg text-xs uppercase tracking-wider transition-colors"
                            >
                                Salvar
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {activeModal === 'status' && (<FormModal title="Alterar Status" onClose={() => setActiveModal(null)} onSave={async () => { 
                let finalS = tempStatus;
                if (isFinished(tempStatus) && tempStatus !== 'Faturado' && tempStatus !== 'Finalizado (Com Cobrança)') {
                    finalS = 'Finalizado (Sem Cobrança)';
                }
                await updateDoc(doc(db, paths.tickets, selectedTicket.id), { status: finalS }); 
                setSelectedTicket(prev => ({...prev, status: finalS}));
                setActiveModal(null); 
            }}><select value={tempStatus} onChange={e => setTempStatus(e.target.value)} className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-bold font-sans">{statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></FormModal>)}
            {activeModal === 'assign' && (<FormModal title="Atribuir Técnicos" onClose={() => setActiveModal(null)} onSave={async () => { await updateDoc(doc(db, paths.tickets, selectedTicket.id), { assignedTo: selectedTicket.assignedTo, assignedToEmail: selectedTicket.assignedToEmail }); setActiveModal(null); }}><AssignedToMultiSelect users={users} assignedUsers={users.filter(u => selectedTicket.assignedTo?.includes(u.uid))} onChange={us => setSelectedTicket({...selectedTicket, assignedTo: us.map(u => u.uid), assignedToEmail: us.map(u => u.email)})} /></FormModal>)}
            {activeModal === 'launch' && (
                <FormModal 
                    title={`Lançar: ${tempLaunchData.fieldName}`} 
                    onClose={() => setActiveModal(null)} 
                    onSave={async () => { 
                        const updated = { ...selectedTicket.technicalData };
                        if (!updated[tempLaunchData.templateId]) updated[tempLaunchData.templateId] = {};
                        updated[tempLaunchData.templateId][tempLaunchData.fieldName] = tempLaunchData.value;
                        await updateDoc(doc(db, paths.tickets, selectedTicket.id), { technicalData: updated });
                        await handleSendComment(true, `**Lançamento: ${tempLaunchData.templateName}**\n**${tempLaunchData.fieldName}:** ${tempLaunchData.value}`);
                        setActiveModal(null);
                    }}
                >
                    {tempLaunchData.fieldType === 'select' ? (
                        <select 
                            value={tempLaunchData.value} 
                            onChange={e => setTempLaunchData({...tempLaunchData, value: e.target.value})} 
                            className="w-full bg-slate-900 border border-slate-700 p-3 rounded text-white outline-none focus:ring-1 focus:ring-cyan-500 font-sans font-bold"
                            autoFocus
                        >
                            <option value="">Selecione...</option>
                            {(tempLaunchData.fieldOptions || '').split(',').map(opt => opt.trim()).filter(Boolean).map((opt, i) => (
                                <option key={i} value={opt}>{opt}</option>
                            ))}
                        </select>
                    ) : (
                        <input 
                            type="text" 
                            value={tempLaunchData.value} 
                            onChange={e => setTempLaunchData({...tempLaunchData, value: e.target.value})} 
                            className="w-full bg-slate-900 border border-slate-700 p-3 rounded text-white outline-none focus:ring-1 focus:ring-cyan-500 font-sans font-sans" 
                            autoFocus 
                        />
                    )}
                </FormModal>
            )}
            {activeModal === 'addTemplate' && (<FormModal title="Adicionar Informação" onClose={() => setActiveModal(null)} onSave={() => setActiveModal(null)}><div className="space-y-2 font-sans font-sans">{launchTemplates.filter(t => !(selectedTicket.addedTemplates || []).includes(t.id)).map(template => (<button key={template.id} onClick={async () => { const added = [...(selectedTicket.addedTemplates || []), template.id]; await updateDoc(doc(db, paths.tickets, selectedTicket.id), { addedTemplates: added }); setActiveModal(null); }} className="w-full text-left bg-slate-700 hover:bg-slate-600 p-4 rounded-lg border border-slate-600 flex justify-between items-center group font-sans font-bold text-cyan-300"><span>{template.name}</span><span className="text-xs text-slate-400 group-hover:text-white transition-colors font-sans font-sans">+ Adicionar</span></button>))}</div></FormModal>)}

            {isCommentModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[150] p-4 font-sans">
                    <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-md border border-slate-700 p-8 text-center animate-fadeIn">
                        {isCommentModalOpen === 'confirmEmail' ? (
                            <>
                                <div className="h-16 w-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-4 mx-auto border border-amber-500/20 text-amber-500">
                                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4">Atenção!</h3>
                                <p className="text-slate-300 mb-8 leading-relaxed text-sm">
                                    Você tem certeza que deseja <strong className="text-amber-400 font-bold uppercase">enviar um e-mail</strong> para o cliente notificando-o sobre este comentário?
                                </p>
                                <div className="flex gap-4">
                                    <button 
                                        onClick={() => setIsCommentModalOpen(true)} 
                                        className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg text-sm transition-colors"
                                    >
                                        Voltar
                                    </button>
                                    <button 
                                        onClick={() => {
                                            handleSendComment(false);
                                            setIsCommentModalOpen(false);
                                        }} 
                                        className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 rounded-lg shadow-lg text-sm transition-colors"
                                    >
                                        Sim, Enviar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="text-2xl font-bold text-white mb-4">Enviar Comentário</h3>
                                <p className="text-slate-300 mb-8 leading-relaxed">
                                    Como deseja tratar este comentário?
                                </p>
                                <div className="flex flex-col gap-3">
                                    <button 
                                        onClick={() => setIsCommentModalOpen('confirmEmail')} 
                                        className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 rounded-lg shadow-lg text-sm transition-colors"
                                    >
                                        Responder (Envia e-mail ao cliente)
                                    </button>
                                    <button 
                                        onClick={() => {
                                            handleSendComment(true);
                                            setIsCommentModalOpen(false);
                                        }} 
                                        className="w-full bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 rounded-lg border border-slate-500 text-sm transition-colors"
                                    >
                                        Não Responder (Apenas interno)
                                    </button>
                                    <button 
                                        onClick={() => setIsCommentModalOpen(false)} 
                                        className="w-full bg-transparent hover:bg-slate-700/50 text-slate-400 hover:text-white py-2 rounded-lg text-xs font-bold transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {isDashboardOpen && (
                <div className="fixed inset-0 bg-black/85 flex justify-center items-center z-[130] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="relative bg-slate-800 rounded-xl border border-slate-700 w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden font-sans">
                        <header className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/80 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <h3 className="font-bold text-xl text-cyan-400 uppercase tracking-widest">Dashboard de Estatísticas</h3>
                            </div>
                            <button onClick={() => { setIsDashboardOpen(false); setDashDrill(null); }} className="text-slate-400 hover:text-white transition-colors text-2xl font-bold">✕</button>
                        </header>

                        <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar bg-slate-900/10 flex-grow font-sans">
                            {/* Linha 1: Cards Rápidos */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Total de Chamados</span>
                                    <span className="text-3xl font-black text-white mt-1 block">{dashboardStats.total}</span>
                                    {dashboardStats.maxSeq > 0 && (
                                        <span className="text-[10px] text-slate-500 block mt-1">Último chamado: A{dashboardStats.maxSeq}</span>
                                    )}
                                </div>
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-cyan-400 uppercase tracking-wider font-bold block">Chamados Ativos</span>
                                    <span className="text-3xl font-black text-cyan-300 mt-1 block">{dashboardStats.active}</span>
                                </div>
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-emerald-400 uppercase tracking-wider font-bold block">Chamados Finalizados</span>
                                    <span className="text-3xl font-black text-emerald-400 mt-1 block">{dashboardStats.finished}</span>
                                </div>
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-orange-400 uppercase tracking-wider font-bold block">Chamados Agendados</span>
                                    <span className="text-3xl font-black text-orange-400 mt-1 block">{dashboardStats.scheduled}</span>
                                </div>
                            </div>

                            {/* Linha 2: Faturamento */}
                            <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider mb-4">Faturamento Acumulado</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                                    <div role="button" title="Ver chamados" onClick={() => setDashDrill({ title: 'Faturamento — Total Geral', tickets: dashboardStats.revenueLists.all, showValue: true })} className="cursor-pointer rounded-lg p-2 -m-2 hover:bg-slate-700/50 transition-colors">
                                        <span className="text-xs text-slate-500 block">Total Geral</span>
                                        <span className="text-2xl font-bold font-mono text-emerald-400">
                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardStats.totalRevenue)}
                                        </span>
                                    </div>
                                    <div role="button" title="Ver chamados" onClick={() => setDashDrill({ title: 'Faturamento — Contrato', tickets: dashboardStats.revenueLists.contrato, showValue: true })} className="cursor-pointer rounded-lg p-2 -m-2 hover:bg-slate-700/50 transition-colors">
                                        <span className="text-xs text-slate-500 block">Faturamento Contrato</span>
                                        <span className="text-lg font-bold font-mono text-cyan-400">
                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardStats.contratoRevenue)}
                                        </span>
                                    </div>
                                    <div role="button" title="Ver chamados" onClick={() => setDashDrill({ title: 'Faturamento — Avulso', tickets: dashboardStats.revenueLists.avulso, showValue: true })} className="cursor-pointer rounded-lg p-2 -m-2 hover:bg-slate-700/50 transition-colors">
                                        <span className="text-xs text-slate-500 block">Faturamento Avulso</span>
                                        <span className="text-lg font-bold font-mono text-teal-400">
                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardStats.avulsoRevenue)}
                                        </span>
                                    </div>
                                </div>
                                {dashboardStats.openRevenue > 0 && (
                                    <p className="text-[11px] text-slate-500 mt-3">
                                        Inclui <button onClick={() => setDashDrill({ title: 'Valores em chamados não finalizados', tickets: dashboardStats.revenueLists.open, showValue: true })} className="text-cyan-400 hover:underline">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardStats.openRevenue)}</button> em chamados ainda não finalizados.
                                    </p>
                                )}
                                {/* Barra de progresso visual do faturamento */}
                                {dashboardStats.totalRevenue > 0 && (
                                    <div className="w-full bg-slate-900 h-2.5 rounded-full mt-4 overflow-hidden flex">
                                        <div 
                                            style={{ width: `${(dashboardStats.contratoRevenue / dashboardStats.totalRevenue) * 100}%` }} 
                                            className="bg-cyan-500 h-full"
                                            title={`Contrato: ${((dashboardStats.contratoRevenue / dashboardStats.totalRevenue) * 100).toFixed(1)}%`}
                                        ></div>
                                        <div 
                                            style={{ width: `${(dashboardStats.avulsoRevenue / dashboardStats.totalRevenue) * 100}%` }} 
                                            className="bg-teal-500 h-full"
                                            title={`Avulso: ${((dashboardStats.avulsoRevenue / dashboardStats.totalRevenue) * 100).toFixed(1)}%`}
                                        ></div>
                                    </div>
                                )}
                            </div>

                            {/* Linha 3: Distribuição */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Por Status */}
                                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                    <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider mb-4">Por Status</h4>
                                    <div className="space-y-3">
                                        {Object.entries(dashboardStats.statusCounts).sort((a,b) => b[1] - a[1]).map(([status, count]) => {
                                            const pct = ((count / dashboardStats.total) * 100).toFixed(1);
                                            const statusColor = statuses.find(s => s.name === status)?.color || '#60a5fa';
                                            return (
                                                <div key={status} role="button" title="Ver chamados" onClick={() => setDashDrill({ title: `Status — ${status}`, tickets: dashboardStats.statusTickets[status] || [] })} className="space-y-1 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-slate-700/50 transition-colors">
                                                    <div className="flex justify-between text-xs text-slate-300">
                                                        <span className="font-semibold">{status}</span>
                                                        <span>{count} ({pct}%)</span>
                                                    </div>
                                                    <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                                                        <div style={{ width: `${pct}%`, backgroundColor: statusColor }} className="h-full"></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Por Técnico */}
                                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                    <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider mb-4">Por Técnico</h4>
                                    <div className="space-y-3">
                                        {Object.entries(dashboardStats.techCounts).sort((a,b) => b[1] - a[1]).map(([tech, count]) => {
                                            const pct = ((count / dashboardStats.total) * 100).toFixed(1);
                                            return (
                                                <div key={tech} role="button" title="Ver chamados" onClick={() => setDashDrill({ title: `Técnico — ${tech}`, tickets: dashboardStats.techTickets[tech] || [] })} className="space-y-1 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-slate-700/50 transition-colors">
                                                    <div className="flex justify-between text-xs text-slate-300">
                                                        <span className="font-semibold capitalize">{tech}</span>
                                                        <span>{count} chamados</span>
                                                    </div>
                                                    <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                                                        <div style={{ width: `${pct}%` }} className="bg-cyan-500 h-full"></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Por Empresa (top 5) */}
                                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                    <div className="flex justify-between items-center mb-4 gap-2">
                                        <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider">
                                            {showAllCompanies ? `Empresas (${dashboardStats.companyRanking.length})` : 'Top Empresas (Mais Atendimentos)'}
                                        </h4>
                                        <button
                                            onClick={() => { setShowAllCompanies(v => !v); setCompanyStatsSearch(''); }}
                                            className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 hover:text-cyan-300 whitespace-nowrap"
                                        >
                                            {showAllCompanies ? 'Ver Top 10' : 'Ver todas'}
                                        </button>
                                    </div>
                                    {showAllCompanies && (
                                        <input
                                            type="text"
                                            value={companyStatsSearch}
                                            onChange={e => setCompanyStatsSearch(e.target.value)}
                                            placeholder="Buscar empresa..."
                                            className="w-full mb-3 bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                        />
                                    )}
                                    <div className={`space-y-3 ${showAllCompanies ? 'max-h-[340px] overflow-y-auto custom-scrollbar pr-1' : ''}`}>
                                        {(showAllCompanies
                                            ? dashboardStats.companyRanking.filter(c => !companyStatsSearch || c.name.toLowerCase().includes(companyStatsSearch.toLowerCase()))
                                            : dashboardStats.companyRanking.slice(0, 10)
                                        ).map(({ name: company, count, registered, tickets: compTickets }) => {
                                            const pct = ((count / dashboardStats.total) * 100).toFixed(1);
                                            return (
                                                <div key={company} role="button" title="Ver chamados" onClick={() => setDashDrill({ title: `Empresa — ${company}`, tickets: compTickets })} className="space-y-1 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-slate-700/50 transition-colors">
                                                    <div className="flex justify-between text-xs text-slate-300">
                                                        <span className="font-semibold truncate max-w-[150px]" title={registered ? company : `${company} (não encontrada no cadastro de clientes)`}>
                                                            {company}{!registered && <span className="text-amber-400" title="Não encontrada no cadastro de clientes"> *</span>}
                                                        </span>
                                                        <span>{count} chamados</span>
                                                    </div>
                                                    <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                                                        <div style={{ width: `${pct}%` }} className="bg-teal-500 h-full"></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {dashboardStats.unlinkedCount > 0 && (
                                        <p className="text-[11px] text-slate-500 mt-4 pt-3 border-t border-slate-700">
                                            <button onClick={() => setDashDrill({ title: 'Chamados sem empresa vinculada', tickets: dashboardStats.unlinkedTickets })} className="text-cyan-400 hover:underline">{dashboardStats.unlinkedCount} chamado(s)</button> sem empresa vinculada (ex.: abertos pelo SMBot) não entram no ranking.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {dashDrill && (() => {
                            const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
                            const getDate = (t) => t.createdAt?.toDate ? t.createdAt.toDate() : (t.createdAt ? new Date(t.createdAt) : null);
                            const list = [...dashDrill.tickets].sort((a, b) => (b.numeroSequencial || 0) - (a.numeroSequencial || 0));
                            const sum = list.reduce((acc, t) => acc + (Number(t.totalAmount) || 0), 0);
                            return (
                                <div className="absolute inset-0 z-10 bg-slate-900/95 backdrop-blur-sm flex flex-col animate-fadeIn">
                                    <div className="p-5 border-b border-slate-700 flex items-center justify-between gap-4 flex-shrink-0">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <button onClick={() => setDashDrill(null)} className="text-slate-400 hover:text-white text-sm font-bold whitespace-nowrap">← Voltar</button>
                                            <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider truncate">{dashDrill.title}</h4>
                                        </div>
                                        <div className="text-xs text-slate-400 whitespace-nowrap">
                                            {list.length} chamado(s){sum > 0 && <> · <span className="text-emerald-400 font-mono font-bold">{fmtBRL(sum)}</span></>}
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
                                        {list.length === 0 && <p className="text-slate-500 text-sm text-center py-10">Nenhum chamado.</p>}
                                        {list.map(t => {
                                            const d = getDate(t);
                                            return (
                                                <button
                                                    key={t.id}
                                                    onClick={() => { setDashDrill(null); setIsDashboardOpen(false); handleOpenTicketDetail(t); }}
                                                    className="w-full text-left bg-slate-800/80 hover:bg-slate-700 border border-slate-700 hover:border-cyan-500/50 rounded-lg p-3 flex items-center gap-3 transition-colors"
                                                >
                                                    <span className="font-mono font-bold text-cyan-400 text-xs w-14 flex-shrink-0">A{t.numeroSequencial}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-slate-200 font-semibold truncate">{t.title}</p>
                                                        <p className="text-[11px] text-slate-500 truncate">
                                                            {getCompanyDisplayName(t.clientCompanyId || t.clientCompany) || 'Sem empresa'} · {t.status}{t.faturamentoTipo ? ` · ${t.faturamentoTipo}` : ''}{d ? ` · ${d.toLocaleDateString('pt-BR')}` : ''}
                                                        </p>
                                                    </div>
                                                    {(Number(t.totalAmount) || 0) > 0 && (
                                                        <span className="font-mono text-xs font-bold text-emerald-400 whitespace-nowrap">{fmtBRL(t.totalAmount)}</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        <footer className="p-4 border-t border-slate-700 bg-slate-800 flex justify-end flex-shrink-0">
                            <button 
                                onClick={() => { setIsDashboardOpen(false); setDashDrill(null); }} 
                                className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-5 rounded-lg text-xs transition-colors font-sans"
                            >
                                Fechar
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {isBillingModalOpen && (
                <FormModal title="Faturar Chamado no VHSYS" onClose={() => setIsBillingModalOpen(false)} onSave={handleInvoiceTicket}>
                    <div className="space-y-4 font-sans p-2">
                        <p className="text-slate-300 text-xs leading-relaxed">
                            Esta ação irá lançar um Contas a Receber no VHSYS para o cliente vinculado a este chamado.
                        </p>
                        <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700 space-y-2 font-bold font-mono">
                            <div className="flex justify-between text-xs">
                                <span className="text-slate-500 font-sans font-bold">Valor a Faturar:</span>
                                <span className="text-emerald-400 font-bold font-mono">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket?.totalAmount || 0)}
                                </span>
                            </div>
                            <div className="flex justify-between text-xs">
                                <span className="text-slate-500 font-sans font-bold">Descrição:</span>
                                <span className="text-white font-sans font-bold">Chamado A${selectedTicket?.numeroSequencial} - {selectedTicket?.title}</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Data de Vencimento</label>
                            <input 
                                type="date" 
                                value={billingDueDate} 
                                onChange={e => setBillingDueDate(e.target.value)} 
                                className="w-full bg-slate-700 border border-slate-600 p-2.5 rounded text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans font-bold"
                            />
                        </div>
                        {billingLoading && (
                            <div className="text-center py-2 text-cyan-400 font-bold animate-pulse text-xs">
                                Lançando contas a receber no VHSYS...
                            </div>
                        )}
                    </div>
                </FormModal>
            )}

            {isFinishedModalOpen && (
                <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-[130] p-4 font-sans backdrop-blur-sm">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-fadeIn">
                        <header className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/80 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 className="font-bold text-xl text-emerald-400 uppercase tracking-widest">Chamados Finalizados</h3>
                            </div>
                            <button onClick={() => setIsFinishedModalOpen(false)} className="text-slate-400 hover:text-white transition-colors text-2xl font-bold">✕</button>
                        </header>
                        
                        {/* Filtros */}
                        <div className="p-6 bg-slate-900/40 border-b border-slate-700 flex-shrink-0 grid grid-cols-1 md:grid-cols-6 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Pesquisar por palavra</label>
                                <input 
                                    type="text" 
                                    placeholder="Título, Nº, Cliente ou Descrição..." 
                                    value={finishedSearch} 
                                    onChange={e => setFinishedSearch(e.target.value)} 
                                    className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500 shadow-inner font-sans"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status Financeiro</label>
                                <select 
                                    value={finishedStatusFilter} 
                                    onChange={e => setFinishedStatusFilter(e.target.value)} 
                                    className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500 shadow-inner font-sans"
                                >
                                    <option value="all">Todos os Chamados</option>
                                    <option value="faturados">Apenas Faturados</option>
                                    <option value="Finalizado (Sem Cobrança)">Sem Cobrança</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Modalidade Cliente</label>
                                <select 
                                    value={finishedClientType} 
                                    onChange={e => setFinishedClientType(e.target.value)} 
                                    className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500 shadow-inner font-sans"
                                >
                                    <option value="all">Todos Clientes</option>
                                    <option value="contrato">Clientes Contrato</option>
                                    <option value="avulso">Clientes Avulso</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Cliente/Empresa</label>
                                <select 
                                    value={finishedClient} 
                                    onChange={e => setFinishedClient(e.target.value)} 
                                    className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500 shadow-inner font-sans"
                                >
                                    <option value="all">Todas Empresas</option>
                                    {dropdownCompanies.map(c => (
                                        <option key={c.id} value={c.name}>{c.displayName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5 justify-end">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Período de Visualização</label>
                                {finishedPeriodMode === 'range' ? (
                                    <div className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-lg p-1 text-xs">
                                        <input
                                            type="date"
                                            value={finishedDateFrom}
                                            max={finishedDateTo || undefined}
                                            onChange={e => setFinishedDateFrom(e.target.value)}
                                            className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500 [color-scheme:dark]"
                                            title="Data inicial"
                                        />
                                        <span className="text-slate-500 text-[10px] font-bold uppercase">até</span>
                                        <input
                                            type="date"
                                            value={finishedDateTo}
                                            min={finishedDateFrom || undefined}
                                            onChange={e => setFinishedDateTo(e.target.value)}
                                            className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500 [color-scheme:dark]"
                                            title="Data final"
                                        />
                                    </div>
                                ) : (
                                <div className="flex items-center justify-between bg-slate-800 border border-slate-700 rounded-lg p-1 text-xs select-none">
                                    <button 
                                        type="button"
                                        onClick={handlePrevMonth} 
                                        className="p-1.5 px-3 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors text-xs font-bold"
                                    >
                                        &lt;
                                    </button>
                                    <div className="text-center px-2">
                                        <span className="text-[8px] text-slate-400 uppercase tracking-widest block leading-none">Visualizando</span>
                                        <span className="text-xs font-bold text-cyan-400 block leading-none mt-1">
                                            {finishedShowAllPeriod ? "Todo o Período" : `${["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"][finishedFilterMonth]} | ${finishedFilterYear}`}
                                        </span>
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={handleNextMonth} 
                                        className="p-1.5 px-3 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors text-xs font-bold"
                                    >
                                        &gt;
                                    </button>
                                </div>
                                )}
                            </div>
                            
                            <div className="flex flex-col gap-1.5 justify-end h-full">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Filtrar por</label>
                                <div className="grid grid-cols-3 bg-slate-800 border border-slate-700 rounded-lg p-1 gap-1">
                                    {[['month', 'Mês'], ['range', 'Datas'], ['all', 'Tudo']].map(([mode, label]) => (
                                        <button
                                            key={mode}
                                            type="button"
                                            onClick={() => setFinishedPeriodMode(mode)}
                                            className={`py-2 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                                finishedPeriodMode === mode
                                                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                                                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Listagem */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-3 custom-scrollbar bg-slate-900/20">
                            {finishedTickets.length === 0 ? (
                                <div className="text-center py-12 text-slate-500 italic text-sm">
                                    Nenhum chamado finalizado correspondente aos filtros.
                                </div>
                            ) : (
                                finishedTickets.slice(0, 100).map(ticket => (
                                    <div 
                                        key={ticket.id} 
                                        onClick={() => { 
                                            handleOpenTicketDetail(ticket); 
                                            setIsFinishedModalOpen(false); 
                                            setCameFromFinishedModal(true);
                                        }} 
                                        className={`p-4 rounded-xl border flex justify-between items-center cursor-pointer transition-all shadow-md group ${
                                            ticket.vhsysVerified 
                                                ? 'bg-emerald-900/20 border-emerald-500/30 hover:bg-emerald-900/30' 
                                                : 'bg-slate-800/80 hover:bg-slate-800/50 border-slate-700/60 hover:border-emerald-500/50'
                                        }`}
                                    >
                                        <div className="min-w-0 flex-1 pr-4">
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            await updateDoc(doc(db, paths.tickets, ticket.id), {
                                                                vhsysVerified: !ticket.vhsysVerified
                                                            });
                                                        } catch (err) {
                                                            console.error("Erro ao atualizar status:", err);
                                                        }
                                                    }}
                                                    className={`w-5 h-5 rounded flex items-center justify-center transition-colors shrink-0 ${
                                                        ticket.vhsysVerified 
                                                            ? 'bg-emerald-500 text-slate-900 shadow-md shadow-emerald-500/20' 
                                                            : 'bg-slate-800 border border-slate-600 hover:border-emerald-400 text-transparent'
                                                    }`}
                                                    title={ticket.vhsysVerified ? "Desmarcar conferência VHSys" : "Marcar como conferido no VHSys"}
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </button>
                                                <h4 className={`text-base font-bold transition-colors truncate ${ticket.vhsysVerified ? 'text-emerald-400' : 'text-slate-200 group-hover:text-emerald-400'}`}>
                                                    A{ticket.numeroSequencial} - {ticket.title}
                                                </h4>
                                            </div>
                                            <p className="text-slate-400 text-xs mt-1 ml-8">
                                                {ticket.clientName} | {getCompanyDisplayName(ticket.clientCompanyId || ticket.clientCompany)}
                                            </p>
                                            <div className="flex gap-3 mt-2 items-center flex-wrap ml-8">
                                                <span 
                                                    className="text-[9px] px-1.5 py-0.5 rounded border border-white/5 font-black uppercase tracking-wider text-white font-sans" 
                                                    style={{ backgroundColor: statuses.find(s => s.name === ticket.status)?.color || (ticket.status?.includes('Contrato') ? '#10b981' : '#06b6d4') }}
                                                >
                                                    {ticket.status}
                                                </span>
                                                <span className="text-[10px] text-slate-500">
                                                    Criado em: {ticket.createdAt ? ticket.createdAt.toDate().toLocaleDateString('pt-BR') : 'Sem data'}
                                                </span>
                                                {(ticket.vhsysRpsNumero || ticket.vhsysNfseId) ? (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 font-bold uppercase tracking-wider text-cyan-400 font-sans" title="Rascunho de NFS-e gerado no VHSYS">
                                                        NFS-e (RPS: {ticket.vhsysRpsNumero || ticket.vhsysNfseId})
                                                    </span>
                                                ) : ticket.vhsysReceitaId ? (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 font-bold uppercase tracking-wider text-emerald-400 font-sans" title="Lançado em Contas a Receber no VHSYS">
                                                        Receita (ID: {ticket.vhsysReceitaId})
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            {ticket.totalAmount > 0 && (
                                                <div className="text-right mr-4">
                                                    <span className="text-[8px] text-slate-500 uppercase tracking-wider font-bold block">Total Cobrado</span>
                                                    <span className="text-sm font-bold font-mono text-emerald-400">
                                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ticket.totalAmount)}
                                                    </span>
                                                </div>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTicketToReopen(ticket);
                                                }}
                                                className="bg-orange-600 hover:bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider py-1.5 px-3 rounded-lg transition-colors font-sans mr-2"
                                            >
                                                Reabrir
                                            </button>
                                            <svg className={`h-5 w-5 shrink-0 transition-colors ${ticket.vhsysVerified ? 'text-emerald-500/50' : 'text-slate-600 group-hover:text-emerald-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        
                        {/* Rodapé */}
                        <div className="p-4 border-t border-slate-700 bg-slate-800 flex justify-between items-center flex-shrink-0">
                            <span className="text-xs text-slate-400">
                                {finishedTickets.length > 100 
                                    ? `Exibindo 100 de ${finishedTickets.length} chamados finalizados` 
                                    : `Total: ${finishedTickets.length} chamados finalizados`
                                }
                            </span>
                            <div className="flex gap-2">
                                <button 
                                    onClick={handlePrintFinishedReport}
                                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 px-4 rounded-lg text-xs transition-colors font-sans flex items-center gap-1.5"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                                    </svg>
                                    Imprimir Relatório
                                </button>
                                <button 
                                    onClick={() => setIsFinishedModalOpen(false)} 
                                    className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-5 rounded-lg text-xs transition-colors font-sans"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isCloseTicketModalOpen && (
                <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-[150] p-4 font-sans backdrop-blur-sm">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-fadeIn">
                        <header className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/80 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 className="font-bold text-lg text-white uppercase tracking-wider">Fechar Chamado</h3>
                            </div>
                            <button onClick={() => setIsCloseTicketModalOpen(false)} className="text-slate-400 hover:text-white transition-colors text-2xl font-bold">✕</button>
                        </header>

                        {/* Cabeçalho de Lançamento (com opção de Customizado) */}
                        <div className="px-6 pt-4 flex justify-between items-center bg-slate-900/20">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                {isCustomService ? "Lançar Serviço não listado" : "Serviço da Tabela de Preços"}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsCustomService(!isCustomService);
                                    setSelectedServiceId('');
                                    setServiceSearchText('');
                                }}
                                className="text-[9px] bg-slate-700/60 hover:bg-slate-700 text-cyan-400 hover:text-cyan-300 font-black px-2.5 py-1 rounded transition-colors uppercase tracking-wider"
                            >
                                {isCustomService ? "✓ Usar Tabela" : "+ Lançar não listado"}
                            </button>
                        </div>

                        {/* Formulário para adicionar serviço */}
                        <div className="px-6 pb-6 pt-2 border-b border-slate-700 bg-slate-900/20 flex-shrink-0 flex flex-col sm:flex-row gap-4 items-end">
                            {isCustomService ? (
                                <>
                                    <div className="flex-1 flex flex-col gap-1.5 w-full">
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Nome do Serviço</label>
                                        <input
                                            type="text"
                                            placeholder="Ex: Instalação de impressora específica..."
                                            value={customServiceName}
                                            onChange={e => setCustomServiceName(e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans"
                                        />
                                    </div>
                                    <div className="w-full sm:w-28 flex flex-col gap-1.5 font-sans">
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Valor (R$)</label>
                                        <input
                                            type="text"
                                            placeholder="150,00"
                                            value={customServiceValue}
                                            onChange={e => setCustomServiceValue(e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 text-center font-sans font-bold"
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="flex-1 flex flex-col gap-1.5 w-full relative">
                                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Buscar Serviço executado</label>
                                    <div className="relative w-full">
                                        <input
                                            type="text"
                                            placeholder="Digite para pesquisar serviço..."
                                            value={serviceSearchText}
                                            onChange={e => {
                                                setServiceSearchText(e.target.value);
                                                setIsServiceDropdownOpen(true);
                                                if (selectedServiceId) setSelectedServiceId('');
                                            }}
                                            onFocus={() => setIsServiceDropdownOpen(true)}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 pl-3 pr-10 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans"
                                        />
                                        {selectedServiceId && (
                                            <span className="absolute right-8 top-3 text-[9px] text-cyan-400 font-black bg-cyan-950/60 px-2 py-0.5 rounded uppercase tracking-wider">
                                                Selecionado
                                            </span>
                                        )}
                                        <button 
                                            type="button"
                                            onClick={() => {
                                                setServiceSearchText('');
                                                setSelectedServiceId('');
                                                setIsServiceDropdownOpen(!isServiceDropdownOpen);
                                            }}
                                            className="absolute right-3 top-3 text-slate-500 hover:text-white"
                                        >
                                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                    </div>
                                    
                                    {isServiceDropdownOpen && (
                                        <div className="absolute left-0 right-0 top-full mt-1 max-h-48 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl overflow-y-auto z-[200] custom-scrollbar text-xs">
                                            {servicesPriceTable.filter(s => 
                                                s.name.toLowerCase().includes(serviceSearchText.toLowerCase()) || 
                                                (s.category && s.category.toLowerCase().includes(serviceSearchText.toLowerCase()))
                                            ).length === 0 ? (
                                                <div className="p-3 text-slate-500 italic text-center">Nenhum serviço encontrado</div>
                                            ) : (
                                                servicesPriceTable.filter(s => 
                                                    s.name.toLowerCase().includes(serviceSearchText.toLowerCase()) || 
                                                    (s.category && s.category.toLowerCase().includes(serviceSearchText.toLowerCase()))
                                                ).map(s => {
                                                    const displayVal = (selectedTicket.priceTableSnapshot && selectedTicket.priceTableSnapshot[s.id] !== undefined) 
                                                        ? selectedTicket.priceTableSnapshot[s.id] 
                                                        : s.value;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={s.id}
                                                            onClick={() => {
                                                                setSelectedServiceId(s.id);
                                                                setServiceSearchText(`[${s.category || 'Geral'}] ${s.name} - R$ ${displayVal}`);
                                                                setIsServiceDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left p-2.5 hover:bg-slate-700 border-b border-slate-700/50 flex justify-between items-center text-slate-200 transition-colors"
                                                        >
                                                            <span>
                                                                <strong className="text-cyan-400 mr-1">[{s.category || 'Geral'}]</strong> {s.name}
                                                            </span>
                                                            <span className="font-mono font-bold text-emerald-400 whitespace-nowrap ml-2">R$ {displayVal}</span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="w-full sm:w-20 flex flex-col gap-1.5">
                                <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Quant.</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={serviceQuantity}
                                    onChange={e => setServiceQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 text-center font-sans font-bold"
                                />
                            </div>
                            <button
                                onClick={handleAddServiceToTicket}
                                disabled={isCustomService ? (!customServiceName.trim() || !customServiceValue) : !selectedServiceId}
                                className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold py-2.5 px-6 rounded-lg text-xs transition-colors uppercase tracking-wider flex items-center justify-center gap-1.5"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                </svg>
                                Lançar
                            </button>
                        </div>

                        {/* Listagem de serviços lançados */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-3 custom-scrollbar bg-slate-900/10">
                            {(!selectedTicket.services || selectedTicket.services.length === 0) ? (
                                <div className="text-center py-8 text-red-400 bg-red-500/5 rounded-xl border border-red-500/10 p-6 flex flex-col items-center gap-2">
                                    <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span className="font-semibold text-sm">Nenhum serviço lançado ainda!</span>
                                    <p className="text-xs text-slate-400 max-w-sm text-center font-sans">Você deve obrigatoriamente adicionar pelo menos um serviço da Tabela de Preços acima antes de fechar este chamado.</p>
                                </div>
                            ) : (
                                <div className="overflow-hidden border border-slate-700/60 rounded-lg bg-slate-900/40">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-slate-800/80 text-slate-400 border-b border-slate-700 font-bold uppercase tracking-wider">
                                                <th className="p-3">Serviço</th>
                                                <th className="p-3 text-right">Unitário</th>
                                                <th className="p-3 text-center">Quant.</th>
                                                <th className="p-3 text-right">Total</th>
                                                <th className="p-3 text-center w-12">Remover</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/40 text-slate-200">
                                            {selectedTicket.services.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                                                    <td className="p-3 font-semibold text-slate-300">{item.name}</td>
                                                    <td className="p-3 text-right font-mono">
                                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.value || 0)}
                                                    </td>
                                                    <td className="p-3 text-center font-bold font-mono">{item.quantity}</td>
                                                    <td className="p-3 text-right font-bold font-mono text-emerald-400">
                                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total || 0)}
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <button
                                                            onClick={() => handleRemoveServiceFromTicket(idx)}
                                                            className="text-slate-500 hover:text-red-400 transition-colors"
                                                            title="Remover Serviço"
                                                        >
                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Rodapé com controle de desconto e botões de fechar */}
                        <div className="p-6 border-t border-slate-700 bg-slate-800 flex-shrink-0 flex flex-col gap-4">
                            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center bg-slate-900/30 p-3 rounded-lg border border-slate-700/50 gap-4">
                                <div className="flex items-center gap-2">
                                    <label className="text-[9px] text-slate-400 uppercase tracking-wider font-bold">Desconto (%):</label>
                                    <input 
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={selectedTicket.discountPercent !== undefined ? selectedTicket.discountPercent : 0}
                                        onChange={e => {
                                            const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                            handleUpdateDiscount(val);
                                        }}
                                        className="w-16 bg-slate-800 border border-slate-700 rounded p-1 text-center text-xs font-bold text-white font-mono"
                                    />
                                    <button 
                                        type="button"
                                        onClick={() => handleUpdateDiscount(5)}
                                        className="text-[9px] bg-slate-700 hover:bg-slate-600 text-cyan-400 font-bold px-2 py-1 rounded transition-colors uppercase tracking-wider"
                                    >
                                        Padrão (5%)
                                    </button>
                                    {(selectedTicket.discountPercent || 0) > 0 && (
                                        <button 
                                            type="button"
                                            onClick={() => handleUpdateDiscount(0)}
                                            className="text-[9px] text-red-400 hover:text-red-300 font-bold px-2 py-1 rounded transition-colors uppercase tracking-wider"
                                        >
                                            Limpar
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col items-end">
                                    {(selectedTicket.discountPercent || 0) > 0 && (
                                        <span className="text-[10px] text-slate-500 line-through font-mono leading-none mb-1">
                                            Subtotal: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket.services?.reduce((sum, s) => sum + s.total, 0) || 0)}
                                        </span>
                                    )}
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-[9px] text-slate-400 uppercase tracking-wider font-bold">Total a Cobrar:</span>
                                        <span className="text-xl font-bold font-mono text-emerald-400">
                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket.totalAmount || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Confirmação Obrigatória da Empresa Vinculada */}
                            <div className="flex flex-col gap-2.5 w-full bg-slate-900/40 p-3.5 rounded-lg border border-cyan-500/20">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                                        <svg className="h-4 w-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                        Empresa Vinculada ao Chamado
                                    </span>
                                    <span className="text-[9px] text-amber-400 font-bold uppercase tracking-wider bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">
                                        Confirmação Obrigatória
                                    </span>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 items-center">
                                    <select
                                        value={closeSelectedCompanyId}
                                        onChange={e => {
                                            setCloseSelectedCompanyId(e.target.value);
                                            setCloseCompanyConfirmed(false); // Exige reconfirmar se trocar de empresa
                                        }}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-xs font-bold outline-none focus:ring-1 focus:ring-cyan-500"
                                    >
                                        <option value="">Selecione uma empresa...</option>
                                        {dropdownCompanies.map(comp => (
                                            <option key={comp.id} value={comp.id}>{comp.displayName}</option>
                                        ))}
                                    </select>
                                </div>

                                <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
                                    <input 
                                        type="checkbox"
                                        checked={closeCompanyConfirmed}
                                        onChange={e => setCloseCompanyConfirmed(e.target.checked)}
                                        className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-cyan-500 focus:ring-0 focus:ring-offset-0 cursor-pointer accent-cyan-500"
                                    />
                                    <span className="text-xs text-slate-200 font-medium">
                                        Confirmo que este chamado pertence à empresa <strong className="text-cyan-300 font-bold">{getCompanyDisplayName(closeSelectedCompanyId || selectedTicket.clientCompanyId || selectedTicket.clientCompany) || 'selecionada'}</strong>
                                    </span>
                                </label>
                            </div>

                            <div className="flex flex-col gap-1.5 w-full bg-slate-900/20 p-3 rounded-lg border border-slate-700/50">
                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Observações de Fechamento / Pagamento</label>
                                <textarea
                                    placeholder="Ex: Já foi pago via PIX, aguardando compensação, ou qualquer outro aviso do técnico..."
                                    value={selectedTicket.closeNotes || ''}
                                    onChange={e => {
                                        const notes = e.target.value;
                                        setSelectedTicket(prev => ({ ...prev, closeNotes: notes }));
                                    }}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-sans"
                                    rows="2"
                                />
                            </div>

                            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-2">
                                <button 
                                    onClick={() => setIsCloseTicketModalOpen(false)} 
                                    className="w-full sm:w-auto px-4 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 text-xs font-semibold transition-colors"
                                >
                                    Cancelar
                                </button>
                                
                                <div className="flex flex-col sm:flex-row items-center gap-2.5 w-full sm:w-auto">
                                    <button 
                                        onClick={() => handleCloseTicket('contrato')} 
                                        disabled={billingLoading || !closeCompanyConfirmed}
                                        className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2.5 px-4 rounded-lg shadow-md text-xs transition-all uppercase tracking-wider flex items-center justify-center gap-1.5 border border-emerald-500/20"
                                        title={!closeCompanyConfirmed ? "Confirme a empresa acima para habilitar o fechamento" : ""}
                                    >
                                        {billingLoading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Fechando...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Finalizado (Sem Cobrança)
                                            </>
                                        )}
                                    </button>
                                    
                                    <button 
                                        onClick={() => setIsInvoiceChoiceModalOpen(true)} 
                                        disabled={billingLoading || !selectedTicket.services || selectedTicket.services.length === 0 || !closeCompanyConfirmed}
                                        className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2.5 px-4 rounded-lg shadow-md text-xs transition-all uppercase tracking-wider flex items-center justify-center gap-1.5 border border-cyan-500/20"
                                        title={!closeCompanyConfirmed ? "Confirme a empresa acima para habilitar o fechamento" : ""}
                                    >
                                        {billingLoading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {FATURAMENTO_PELO_FINANCEIRO ? 'Finalizando...' : 'Faturando...'}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Finalizado (Com Cobrança)
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Escolha: Emitir NFS-e ou Faturar Sem Nota */}
            {isInvoiceChoiceModalOpen && (
                <div className="fixed inset-0 bg-black/85 flex justify-center items-center z-[200] p-4 font-sans backdrop-blur-md animate-fadeIn">
                    <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-md p-6 shadow-2xl flex flex-col gap-5">
                        <header className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-bold text-base text-white">{FATURAMENTO_PELO_FINANCEIRO ? 'O cliente precisa de Nota Fiscal?' : 'Emitir Nota Fiscal (NFS-e)?'}</h3>
                                <p className="text-xs text-slate-400">{FATURAMENTO_PELO_FINANCEIRO ? 'Sua indicação vai para o Financeiro, que fará o faturamento' : 'Defina como esse chamado será faturado no VHSYS'}</p>
                            </div>
                        </header>

                        <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-700/50 space-y-2 text-xs">
                            <div className="flex justify-between items-center text-slate-300">
                                <span>Total do Chamado:</span>
                                <span className="font-bold font-mono text-emerald-400 text-sm">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTicket?.totalAmount || 0)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400 text-[11px]">
                                <span>Cliente:</span>
                                <span className="font-semibold text-slate-200 truncate max-w-[200px]">
                                    {getCompanyDisplayName(closeSelectedCompanyId || selectedTicket?.clientCompanyId || selectedTicket?.clientCompany)}
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                            <button
                                type="button"
                                disabled={billingLoading}
                                onClick={async () => {
                                    setIsInvoiceChoiceModalOpen(false);
                                    await handleCloseTicket('avulso', false); // Apenas Contas a Receber
                                }}
                                className="flex flex-col items-center justify-center gap-1.5 p-3.5 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-bold text-xs transition-all hover:border-slate-500 shadow-md group"
                            >
                                <span className="text-sm font-extrabold text-amber-400">NÃO</span>
                                <span className="text-[10px] text-slate-300 font-normal group-hover:text-white">{FATURAMENTO_PELO_FINANCEIRO ? 'Sem nota fiscal' : 'Contas a Receber'}</span>
                                <span className="text-[8px] text-slate-400 font-mono">{FATURAMENTO_PELO_FINANCEIRO ? '(Indicação ao Financeiro)' : '(Direto no Financeiro)'}</span>
                            </button>

                            <button
                                type="button"
                                disabled={billingLoading}
                                onClick={async () => {
                                    setIsInvoiceChoiceModalOpen(false);
                                    await handleCloseTicket('avulso', true); // COM NOTA
                                }}
                                className="flex flex-col items-center justify-center gap-1.5 p-3.5 bg-cyan-600 hover:bg-cyan-500 border border-cyan-400/40 rounded-xl text-white font-bold text-xs transition-all shadow-lg shadow-cyan-900/40 group"
                            >
                                <span className="text-sm font-extrabold text-white">SIM</span>
                                <span className="text-[10px] text-cyan-100 font-normal">{FATURAMENTO_PELO_FINANCEIRO ? 'Com nota fiscal' : 'Gerar NFS-e'}</span>
                                <span className="text-[8px] text-cyan-200 font-mono">{FATURAMENTO_PELO_FINANCEIRO ? '(Indicação ao Financeiro)' : '(Rascunho no VHSYS)'}</span>
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsInvoiceChoiceModalOpen(false)}
                            className="w-full text-center text-xs text-slate-500 hover:text-slate-300 transition-colors pt-1"
                        >
                            Voltar / Cancelar
                        </button>
                    </div>
                </div>
            )}

            {isFormOpen && (
                <FormModal onClose={() => setIsFormOpen(false)} title={formMode === 'add' ? "Abrir Novo Chamado" : "Editar Chamado"} onSave={handleSaveTicket}>
                    <form className="space-y-4 font-sans font-sans font-sans font-sans">

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block">Nome do Usuário</label>
                                {filteredUsersForCompany.length > 0 ? (
                                    <select 
                                        value={newTicket.clientName} 
                                        onChange={e => {
                                            const u = filteredUsersForCompany.find(user => user.email === e.target.value);
                                            setNewTicket({
                                                ...newTicket, 
                                                clientName: e.target.value,
                                                clientEmail: u?.email || '',
                                                clientWhatsApp: u?.phone || ''
                                            });
                                        }}
                                        className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans outline-none focus:ring-1 focus:ring-cyan-500"
                                    >
                                        <option value="">Selecione ou digite abaixo</option>
                                        {filteredUsersForCompany.map(u => (
                                            <option key={u.uid} value={u.email}>{u.email}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input 
                                        type="text" 
                                        placeholder="Nome do cliente"
                                        value={newTicket.clientName} 
                                        onChange={e => setNewTicket({...newTicket, clientName: e.target.value})} 
                                        className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans" 
                                    />
                                )}
                                {filteredUsersForCompany.length > 0 && (
                                    <input 
                                        type="text" 
                                        placeholder="Ou digite outro nome..."
                                        value={newTicket.clientName} 
                                        onChange={e => setNewTicket({...newTicket, clientName: e.target.value})} 
                                        className="w-full bg-slate-800/30 border-none p-2 mt-1 rounded text-xs text-slate-400 font-sans italic" 
                                    />
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block font-sans">E-mail</label>
                                <input 
                                    type="email" 
                                    value={newTicket.clientEmail} 
                                    onChange={e => setNewTicket({...newTicket, clientEmail: e.target.value})} 
                                    className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans" 
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block">Empresa</label>
                                {allowedCompanies === null || dropdownCompanies.length > 1 ? (
                                    <select 
                                        value={newTicket.clientCompany} 
                                        onChange={e => {
                                            const selectedComp = dropdownCompanies.find(c => c.name === e.target.value || c.displayName === e.target.value);
                                            
                                            let autoEmail = newTicket.clientEmail;
                                            if (selectedComp) {
                                                const fullComp = allCompanyList.find(c => c.id === selectedComp.id);
                                                if (fullComp) {
                                                    const rawEmail = fullComp.email || fullComp.emailNFe || '';
                                                    const firstEmail = rawEmail.split(/[,;]/)[0].trim();
                                                    if (firstEmail) autoEmail = firstEmail;
                                                }
                                            }

                                            setNewTicket({
                                                ...newTicket,
                                                clientCompany: selectedComp ? selectedComp.name : e.target.value,
                                                clientCompanyId: selectedComp ? selectedComp.id : '',
                                                clientEmail: autoEmail
                                            });
                                        }}
                                        className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans outline-none focus:ring-1 focus:ring-cyan-500"
                                        required
                                    >
                                        <option value="">Selecione a Empresa</option>
                                        {dropdownCompanies.map(company => (
                                            <option key={company.id} value={company.name}>{company.displayName}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input 
                                        type="text" 
                                        value={getCompanyDisplayName(newTicket.clientCompanyId || newTicket.clientCompany)} 
                                        readOnly 
                                        className="w-full bg-slate-800 border border-slate-700 p-3 rounded text-slate-400 font-sans cursor-not-allowed" 
                                    />
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block">WhatsApp</label>
                                <input 
                                    type="text" 
                                    value={newTicket.clientWhatsApp} 
                                    onChange={e => setNewTicket({...newTicket, clientWhatsApp: e.target.value})} 
                                    className="w-full bg-slate-700 border border-slate-600 p-3 rounded text-white font-sans" 
                                />
                            </div>
                        </div>
                        <div font-sans><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block font-sans">Título</label><input type="text" value={newTicket.title} onChange={e => setNewTicket({...newTicket, title: e.target.value})} className="w-full bg-slate-900 border border-slate-700 p-3 rounded text-white shadow-inner font-sans" required /></div>
                        <div font-sans><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block font-sans">Descrição</label><textarea value={newTicket.description} onChange={e => setNewTicket({...newTicket, description: e.target.value})} rows="4" className="w-full bg-slate-900 border border-slate-700 p-3 rounded text-white shadow-inner font-sans" required /></div>
                         
                         {/* --- ÁREA DE ANEXOS NO FORMULÁRIO --- */}
                         <div className="font-sans">
                             <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block font-sans">Anexar Arquivos</label>
                             
                             {/* Anexos já enviados (Edição) */}
                             {newTicket.attachments && newTicket.attachments.length > 0 && (
                                 <div className="space-y-2 mb-3">
                                     <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Arquivos já anexados:</p>
                                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                         {newTicket.attachments.map((file, idx) => (
                                             <div key={idx} className="flex justify-between items-center bg-slate-800 p-2 rounded-lg border border-slate-700 text-xs">
                                                 <span className="truncate flex-1 mr-2">{file.name}</span>
                                                 <button 
                                                     type="button" 
                                                     onClick={() => handleDeleteAttachment(idx)} 
                                                     className="text-red-400 hover:text-red-300 font-bold px-2 py-1"
                                                 >
                                                     Remover
                                                 </button>
                                             </div>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* Novos arquivos na fila de envio */}
                             {selectedFiles.length > 0 && (
                                 <div className="space-y-2 mb-3">
                                     <p className="text-[9px] font-bold text-cyan-400 uppercase tracking-wider">Novos arquivos para envio:</p>
                                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                         {selectedFiles.map((file, idx) => (
                                             <div key={idx} className="flex justify-between items-center bg-slate-800/80 p-2 rounded-lg border border-cyan-500/20 text-xs">
                                                 <span className="truncate flex-1 mr-2">{file.name}</span>
                                                 <button 
                                                     type="button" 
                                                     onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))} 
                                                     className="text-slate-400 hover:text-white font-bold px-2 py-1"
                                                 >
                                                     Remover
                                                 </button>
                                             </div>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* Botão de Upload */}
                             <div className="flex items-center gap-3">
                                 <label className="cursor-pointer bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-slate-500 text-white text-[11px] font-bold py-2.5 px-4 rounded-lg flex items-center gap-2 transition-all">
                                     <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                         <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                     </svg>
                                     Selecionar Arquivos...
                                     <input 
                                         type="file" 
                                         multiple 
                                         onChange={e => {
                                             if (e.target.files) {
                                                 setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]);
                                             }
                                         }} 
                                         className="hidden" 
                                     />
                                 </label>
                                 {uploading && (
                                     <span className="text-xs text-cyan-400 animate-pulse font-bold">Enviando arquivos para o Storage...</span>
                                 )}
                             </div>
                         </div>
                    </form>
                </FormModal>
            )}

            <StatusManagerModal
                isOpen={isStatusManagerOpen}
                onClose={() => setIsStatusManagerOpen(false)}
                statuses={statuses}
                onAdd={async (newStatus) => {
                    try {
                        await addDoc(collection(db, paths.statuses), newStatus);
                    } catch (err) {
                        console.error("Erro ao adicionar status:", err);
                        alert("Erro ao adicionar status: " + err.message);
                    }
                }}
                onUpdate={async (id, updatedStatus) => {
                    try {
                        await updateDoc(doc(db, paths.statuses, id), updatedStatus);
                    } catch (err) {
                        console.error("Erro ao atualizar status:", err);
                        alert("Erro ao atualizar status: " + err.message);
                    }
                }}
                onDelete={async (id) => {
                    try {
                        await deleteDoc(doc(db, paths.statuses, id));
                    } catch (err) {
                        console.error("Erro ao excluir status:", err);
                    }
                }}
            />

            {permissionLevel === 'edit' && viewMode === 'list' && isAllowed('open-ticket') && (
                <button 
                    onClick={() => { 
                        setFormMode('add'); 
                        const currentUserData = users.find(u => u.email === auth.currentUser?.email);
                        setNewTicket({ 
                            title: '', 
                            description: '', 
                            priority: 'low', 
                            assignedTo: [], 
                            assignedToEmail: [], 
                            status: '', 
                            clientName: '', 
                            clientEmail: auth.currentUser?.email || '', 
                            clientWhatsApp: currentUserData?.phone || '', 
                            clientCompany: allowedCompanies?.length === 1 ? allowedCompanies[0] : '',
                            attachments: []
                        });
                        setIsFormOpen(true); 
                    }} 
                    className="fixed bottom-10 right-10 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full h-14 w-14 shadow-2xl transition-all transform hover:scale-110 active:scale-95 z-30 flex items-center justify-center font-sans"
                >
                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                </button>
            )}
        </div>
    );
}